import { createApiClient } from '../../../../api/apiClient';
import { formatUserName } from '../../../../utils/emailTouserName';
import { TICKETING_FILTER_OPTIONS_MOCK } from '../../constants/ticketingFilterOptionsMock';
import { TICKETING_OVERVIEW_MOCK } from '../../constants/ticketingOverviewMock';
import { TICKETING_VOLUME_TRENDS_MOCK } from '../../constants/ticketingVolumeTrendsMock';
import { TICKETING_TEAM_WORKLOAD_MOCK } from '../../constants/ticketingTeamWorkloadMock';
import { TICKETING_RCA_MOCK } from '../../constants/ticketingRcaMock';
import { TICKETING_CATEGORIES_MOCK } from '../../constants/ticketingCategoriesMock';
import { TICKETING_SUBMITTERS_LOCATIONS_MOCK } from '../../constants/ticketingSubmittersLocationsMock';
import type {
	TicketingAnalyticsCustomDateRange,
	TicketingAnalyticsSelectedFilters,
	TicketingTimePeriodFilterPayload,
	TicketingCategoriesPayload,
	TicketingCategoriesRequest,
	TicketingLifecyclePayload,
	TicketingLifecycleRequest,
	TicketingOverviewPayload,
	TicketingOverviewRequest,
	TicketingRcaPayload,
	TicketingRcaRequest,
	TicketingSubmittersLocationsPayload,
	TicketingSubmittersLocationsRequest,
	TicketingTeamWorkloadPayload,
	TicketingTeamWorkloadRequest,
	TicketingVolumeTrendsPayload,
	TicketingVolumeTrendsRequest,
	LifecycleStatusData,
} from '../../components/types';

export const TICKETING_ANALYTICS_REPORT_LINK = '/navigator-reports/analytics/ticketing';

const api = createApiClient();

const FILTER_OPTIONS_ENDPOINT = '/ticketing-analytics/filter-options';
const OVERVIEW_ENDPOINT = '/ticketing-analytics/overview';
const VOLUME_TRENDS_ENDPOINT = '/ticketing-analytics/volume-trends';
const TEAM_WORKLOAD_ENDPOINT = '/ticketing-analytics/team-workload';
const RCA_ENDPOINT = '/ticketing-analytics/rca';
const LIFECYCLE_ENDPOINT = '/ticketing-analytics/lifecycle';
const CATEGORIES_ENDPOINT = '/ticketing-analytics/categories';
const SUBMITTERS_LOCATIONS_ENDPOINT = '/ticketing-analytics/submitters-and-locations';

const TICKETING_ANALYTICS_BASE_ENDPOINT = '/analytics/ticketing';
const SEMANTIC_OVERVIEW_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/overview`;
const SEMANTIC_AGING_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/aging`;
const SEMANTIC_STATUS_DISTRIBUTION_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/status-distribution`;
const SEMANTIC_STATUS_AVG_TIME_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/status-avg-time`;
const SEMANTIC_ESCALATION_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/escalation`;
const SEMANTIC_DUPLICATE_FLAGS_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/duplicate-flags`;
const SEMANTIC_MERGE_STATS_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/merge-stats`;
// NOTE: The semantic-layer /analytics/ticketing/category-breakdown returns numeric IDs only
// (categoryId: 1, subCategoryId: 9). Use the dedicated reporting endpoints instead which
// return { id, name } pairs with proper display names.
const SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/category-breakdown`;
const SEMANTIC_CATEGORY_TREND_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/category-trend`;
const SEMANTIC_VOLUME_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/volume`;
const SEMANTIC_CYCLE_TIMES_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/cycle-times`;
const SEMANTIC_STALE_TICKETS_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/stale-tickets`;
const SEMANTIC_ADO_CONVERSION_RATE_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/ado-conversion-rate`;
const SEMANTIC_AGENT_WORKLOAD_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/agent-workload`;
const SEMANTIC_AGENT_STATUS_AVG_TIME_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/agent-status-avg-time`;
const SEMANTIC_TOP_SUBMITTERS_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/top-submitters`;
const SEMANTIC_TOP_LOCATIONS_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/top-locations`;
const SEMANTIC_ROLE_BREAKDOWN_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/role-breakdown`;
const SEMANTIC_RCA_BREAKDOWN_ENDPOINT = `${TICKETING_ANALYTICS_BASE_ENDPOINT}/rca-breakdown`;

const FISCAL_DATE_RANGE_ENDPOINT = '/analytics/fiscal-date-range';

// ── Dedicated Reporting Endpoints (DEPRECATED - Not implemented on backend) ─────────────
// These would return proper { id: number, name: string } pairs but have CORS issues.
// Currently using semantic endpoints instead which return numeric IDs.
// TODO: Implement on backend if needed for display name support
// const REPORTING_CATEGORY_BREAKDOWN_ENDPOINT = '/reporting/category-breakdown';
// const REPORTING_CATEGORY_TREND_ENDPOINT = '/reporting/category-trend';

type TicketingRequestWithFilters = {
	timePeriod: string;
	customDateRange?: TicketingAnalyticsCustomDateRange;
	timePeriodFilter?: TicketingTimePeriodFilterPayload;
	selectedFilters: TicketingAnalyticsSelectedFilters;
	/** Available locations for auto-population when none selected (for API logic) */
	availableLocations?: any[];
	/** Available regions for auto-population when none selected (mirrors locationIds pattern) */
	availableRegions?: any[];
	/** Available markets for auto-population when none selected (mirrors locationIds pattern) */
	availableMarkets?: any[];
	/** Global Team selector scope — omitted/empty means "All" teams */
	teamIds?: string[];
};

/**
 * ID-to-name lookup maps built from Redux `submitTicketCategoryConfig`.
 * Passed from TicketingAnalyticsDashboard into all semantic-layer fetch functions
 * so that numeric categoryId / subCategoryId values from the API are resolved
 * to human-readable names before being stored in the payload.
 *
 * Example:
 *   categoryIdToName: { 9: "System Issue", 1: "Access Request" }
 *   subCategoryIdToName: { 20: "Navigator", 1: "New Access" }
 */
export type CategoryLookup = {
	categoryIdToName: Record<number, string>;
	subCategoryIdToName: Record<number, string>;
};

/**
 * Resolve a raw categoryId value (may be a number or a string name) to a
 * human-readable display name using the provided lookup map.
 *
 * If the value is already a string that isn't purely numeric, it is returned
 * as-is (the API already sent a name). If it is a number or numeric string and
 * a matching entry exists in the lookup, the name is returned.
 * Falls back to the raw stringified value when no lookup entry is found.
 */
const resolveName = (
	rawId: unknown,
	lookup: Record<number, string> | undefined,
	fallback = '',
): string => {
	const raw = typeof rawId === 'string' ? rawId.trim() : String(rawId ?? '').trim();
	if (!raw) return fallback;

	// Already a human-readable string (not purely numeric) — return as-is
	if (!/^\d+$/.test(raw)) return raw;

	const numId = Number(raw);
	if (lookup && lookup[numId] !== undefined) {
		return lookup[numId];
	}

	// No lookup entry — return the raw string (keeps backward compat)
	return raw;
};

const mapFilterIds = (items: Array<{ id: string | number }>) => items.map((item) => item.id);

const buildRequestBody = async (request: TicketingRequestWithFilters) => {
	const { fromDate, toDate } = await resolveFiscalDates(request);

	if (!fromDate || !toDate) {
		throw new Error(`Connection lost. Check your network and try again.`);
	}

	// Only include filter values if user explicitly selected them.
	// Omit empty arrays to send clean requests when no filters are applied.
	const selectedFilters: any = {};

	if (request.selectedFilters.regions.length > 0) {
		selectedFilters.regions = mapFilterIds(request.selectedFilters.regions);
	}

	if (request.selectedFilters.markets.length > 0) {
		selectedFilters.markets = mapFilterIds(request.selectedFilters.markets);
	}

	if (request.selectedFilters.locations.length > 0) {
		selectedFilters.locations = mapFilterIds(request.selectedFilters.locations);
	}

	if (request.selectedFilters.categories.length > 0) {
		selectedFilters.categories = mapFilterIds(request.selectedFilters.categories);
	}

	if (request.selectedFilters.subCategories.length > 0) {
		selectedFilters.subCategories = mapFilterIds(request.selectedFilters.subCategories);
	}

	if (request.selectedFilters.statuses.length > 0) {
		selectedFilters.statuses = mapFilterIds(request.selectedFilters.statuses);
	}

	if (request.selectedFilters.resolutionPaths.length > 0) {
		selectedFilters.resolutionPaths = mapFilterIds(request.selectedFilters.resolutionPaths);
	}

	if (request.selectedFilters.urgencies.length > 0) {
		selectedFilters.urgencies = mapFilterIds(request.selectedFilters.urgencies);
	}

	if (request.selectedFilters.assignedTo.length > 0) {
		selectedFilters.assignedTo = mapFilterIds(request.selectedFilters.assignedTo);
	}

	return {
		fromDate,
		toDate,
		selectedFilters,
	};
};

type TicketingAnalyticsSemanticFilter = {
	fromDate: string;
	toDate: string;
	regions?: string[];
	markets?: string[];
	statuses?: string[];
	categories?: string[];
	subCategories?: string[];
	urgencies?: string[];
	locationIds?: string[];
	assignees?: string[];
	teamIds?: string[];
	tertiaryIds?: string[];
	granularity?: 'daily' | 'weekly' | 'monthly';
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapPayload = <T>(value: unknown): T | null => {
	if (isObject(value) && isObject(value.data)) {
		return value.data as T;
	}
	if (isObject(value)) {
		return value as T;
	}
	return null;
};

const getWithFallback = async <T>(endpoint: string, fallback: T): Promise<T> => {
	try {
		const response = await api.get(endpoint);
		return unwrapPayload<T>(response?.data) ?? fallback;
	} catch {
		return fallback;
	}
};

const postWithFallback = async <T>(endpoint: string, body: TicketingRequestWithFilters, fallback: T): Promise<T> => {
	try {
		const requestBody = await buildRequestBody(body);
		const response = await api.post(endpoint, requestBody);
		return unwrapPayload<T>(response?.data) ?? fallback;
	} catch {
		return fallback;
	}
};

const asObject = (value: unknown): Record<string, unknown> | null =>
	isObject(value) ? value : null;

const asArray = <T = unknown>(value: unknown): T[] =>
	Array.isArray(value) ? (value as T[]) : [];

const asNumber = (value: unknown, fallback = 0): number => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === 'string') {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}

	return fallback;
};

const asString = (value: unknown, fallback = ''): string =>
	typeof value === 'string' ? value : fallback;

const maybeIsoDate = (value: string | null | undefined): string | undefined => {
	if (!value) {
		return undefined;
	}

	const [yearRaw, secondRaw, thirdRaw] = value.split('-');
	if (!yearRaw || !secondRaw || !thirdRaw) {
		return undefined;
	}

	const year = Number(yearRaw);
	const second = Number(secondRaw);
	const third = Number(thirdRaw);
	if (!Number.isInteger(year) || !Number.isInteger(second) || !Number.isInteger(third)) {
		return undefined;
	}

	// Custom range dates arrive as YYYY-MM-DD (standard ISO, set by the date picker).
	// Always treat second segment as month and third segment as day.
	// The legacy YYYY-DD-MM swap logic (second > 12) is intentionally removed because:
	//   - It only worked when day > 12 (e.g. Aug 15 → second=15 > 12 → swap)
	//   - It silently broke dates where day ≤ 12 (e.g. Jul 1 → second=07 ≤ 12 → treated as month=7, day=1 which happens to be correct by accident, but Aug 7 → second=07 ≤ 12 → month=07, day=08 which is July 8 not Aug 7)
	const month = second;
	const day = third;

	return `${yearRaw}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00Z`;
};

// End-of-day variant: used for custom range toDate so the full end day is included.
const maybeIsoEndDate = (value: string | null | undefined): string | undefined => {
	if (!value) {
		return undefined;
	}
	const [yearRaw, secondRaw, thirdRaw] = value.split('-');
	if (!yearRaw || !secondRaw || !thirdRaw) {
		return undefined;
	}
	const year = Number(yearRaw);
	const second = Number(secondRaw);
	const third = Number(thirdRaw);
	if (!Number.isInteger(year) || !Number.isInteger(second) || !Number.isInteger(third)) {
		return undefined;
	}
	return `${yearRaw}-${String(second).padStart(2, '0')}-${String(third).padStart(2, '0')}T23:59:59Z`;
};

const normalizeFilterValues = (
	items: Array<{ id: string | number; displayText?: string }>,
	preferDisplayText = false,
): string[] => {
	const values = items
		.map((item) => {
			if (preferDisplayText && item.displayText) {
				return item.displayText;
			}
			if (typeof item.id === 'string') {
				return item.id;
			}
			if (typeof item.id === 'number') {
				return String(item.id);
			}
			return '';
		})
		.filter((value) => value.length > 0 && value.toLowerCase() !== 'all');

	return Array.from(new Set(values));
};

/**
 * Normalizes filter values, returning undefined when the result is empty.
 * This indicates "All" was selected, and we should omit the filter from the API request
 * (optional fields). An empty array would be interpreted by the backend as "show no items",
 * whereas undefined means "no filter applied" (show all items).
 */
const normalizeFilterValuesOptional = (
	items: Array<{ id: string | number; displayText?: string }>,
	preferDisplayText = false,
): string[] | undefined => {
	const normalized = normalizeFilterValues(items, preferDisplayText);
	return normalized.length > 0 ? normalized : undefined;
};

const mapTimePeriodToGranularity = (timePeriod: string): 'daily' | 'weekly' | 'monthly' => {
	if (timePeriod === 'day') {
		return 'daily';
	}

	if (timePeriod === 'week') {
		return 'weekly';
	}

	return 'monthly';
};

interface FiscalDateRangeResponse {
	fromDate: string;
	toDate: string;
	label: string;
}

// Cache fiscal-date-range results to avoid redundant calls within the same filter combination
let fiscalDateCache: { key: string; promise: Promise<FiscalDateRangeResponse | null> } | null = null;

const fetchFiscalDateRange = (
	filter: string,
	value: string,
	year?: string,
): Promise<FiscalDateRangeResponse | null> => {
	const cacheKey = `${filter}|${value}|${year ?? ''}`;

	if (fiscalDateCache && fiscalDateCache.key === cacheKey) {
		return fiscalDateCache.promise;
	}

	const promise = (async (): Promise<FiscalDateRangeResponse | null> => {
		try {
			const body: Record<string, string> = { filter, value };
			if (year) {
				body.year = year;
			}
			console.log('[TicketingAnalytics] Calling fiscal-date-range:', body);
			const response = await api.post(FISCAL_DATE_RANGE_ENDPOINT, body);
			const data = response?.data?.data ?? response?.data;
			if (data?.fromDate && data?.toDate) {
				console.log('[TicketingAnalytics] Fiscal date resolved:', data.fromDate, '->', data.toDate);
				return { fromDate: data.fromDate, toDate: data.toDate, label: data.label ?? '' };
			}
			console.warn('[TicketingAnalytics] fiscal-date-range returned no dates:', response?.data);
			return null;
		} catch (err) {
			console.warn('[TicketingAnalytics] fiscal-date-range API failed:', err);
			return null;
		}
	})();

	fiscalDateCache = { key: cacheKey, promise };
	return promise;
};

const resolveFiscalDates = async (
	request: TicketingRequestWithFilters,
): Promise<{ fromDate?: string; toDate?: string }> => {
	if (request.timePeriod === 'custom') {
		const tpf = request.timePeriodFilter;
		if (tpf && tpf.filter === 'custom' && typeof tpf.value === 'object') {
			return {
				fromDate: maybeIsoDate(tpf.value.start),
				toDate: maybeIsoEndDate(tpf.value.end),
			};
		}
		return {
			fromDate: maybeIsoDate(request.customDateRange?.startDate),
			toDate: maybeIsoEndDate(request.customDateRange?.endDate),
		};
	}

	const tpf = request.timePeriodFilter;
	if (!tpf || tpf.filter === 'custom') {
		console.warn('[TicketingAnalytics] No timePeriodFilter available for non-custom period:', request.timePeriod);
		return {};
	}

	const value = typeof tpf.value === 'object' ? '' : String(tpf.value);
	const year = 'year' in tpf ? tpf.year : undefined;

	if (!value) {
		console.warn('[TicketingAnalytics] timePeriodFilter value is empty:', tpf);
		return {};
	}

	const range = await fetchFiscalDateRange(tpf.filter, value, year);
	if (range) {
		return { fromDate: range.fromDate, toDate: range.toDate };
	}

	return {};
};

const buildSemanticFilterBody = async (request: TicketingRequestWithFilters): Promise<TicketingAnalyticsSemanticFilter> => {
	const { fromDate, toDate } = await resolveFiscalDates(request);

	if (!fromDate || !toDate) {
		throw new Error(`Connection lost. Check your network and try again.`);
	}

	// Only include regions/markets/locations if user explicitly selected them.
	// When no filters are selected, omit these fields from the request payload.
	// This ensures clean requests without unnecessary data when no filters are applied.

	const filterBody: TicketingAnalyticsSemanticFilter = {
		fromDate,
		toDate,
		granularity: mapTimePeriodToGranularity(request.timePeriod),
	};

	// Only add optional filters if user has explicitly selected values
	const regionValues = normalizeFilterValuesOptional(request.selectedFilters.regions, true);
	if (regionValues) {
		filterBody.regions = regionValues;
	}

	const marketValues = normalizeFilterValuesOptional(request.selectedFilters.markets, true);
	if (marketValues) {
		filterBody.markets = marketValues;
	}

	const locationValues = normalizeFilterValuesOptional(request.selectedFilters.locations, false);
	if (locationValues) {
		filterBody.locationIds = locationValues;
	}

	// Statuses and urgencies: include only if user selected specific values
	const statusValues = normalizeFilterValuesOptional(request.selectedFilters.statuses, true);
	if (statusValues) {
		filterBody.statuses = statusValues;
	}

	const urgencyValues = normalizeFilterValuesOptional(request.selectedFilters.urgencies, true);
	if (urgencyValues) {
		filterBody.urgencies = urgencyValues;
	}

	// Categories and subcategories: optional filters
	const categoryValues = normalizeFilterValuesOptional(request.selectedFilters.categories, false);
	if (categoryValues) {
		filterBody.categories = categoryValues;
	}

	const subCategoryValues = normalizeFilterValuesOptional(request.selectedFilters.subCategories, false);
	if (subCategoryValues) {
		filterBody.subCategories = subCategoryValues;
	}

	// Assignees: optional filter
	const assigneeValues = normalizeFilterValuesOptional(request.selectedFilters.assignedTo, false);
	if (assigneeValues) {
		filterBody.assignees = assigneeValues;
	}

	// Tertiary: optional filter, same shape as categories/subCategories
	const tertiaryValues = normalizeFilterValuesOptional(request.selectedFilters.tertiaries ?? [], false);
	if (tertiaryValues) {
		filterBody.tertiaryIds = tertiaryValues;
	}

	// Global Team selector scope — explicit request field, not part of the filter bar
	if (request.teamIds && request.teamIds.length > 0) {
		filterBody.teamIds = request.teamIds;
	}

	return filterBody;
};

const postSemantic = async (endpoint: string, body: TicketingAnalyticsSemanticFilter): Promise<Record<string, unknown> | null> => {
	const response = await api.post(endpoint, body);
	return asObject(unwrapPayload<Record<string, unknown>>(response?.data));
};

const postSemanticWithParams = async (
	endpoint: string,
	body: TicketingAnalyticsSemanticFilter,
	params: Record<string, string | number>,
): Promise<Record<string, unknown> | null> => {
	const response = await api.post(endpoint, body, { params });
	return asObject(unwrapPayload<Record<string, unknown>>(response?.data));
};

const humanizeDuration = (minutes: number): string => {
	if (!Number.isFinite(minutes) || minutes <= 0) {
		return '—';
	}

	if (minutes >= 1440) {
		return `${(minutes / 1440).toFixed(1)} days`;
	}

	return `${(minutes / 60).toFixed(1)} hrs`;
};

const roundToOneDecimal = (value: number): number =>
	Number.isFinite(value) ? Number(value.toFixed(1)) : 0;

const roundToWhole = (value: number): number =>
	Number.isFinite(value) ? Math.round(value) : 0;

const safePercent = (numerator: number, denominator: number): number => {
	if (!denominator) {
		return 0;
	}

	return roundToOneDecimal((numerator / denominator) * 100);
};

const toHours = (minutes: number): number => roundToOneDecimal(minutes / 60);

const toDisplayTimeValue = (minutes: number): { value: number; unit: 'hours' | 'days' } => {
	const hours = toHours(minutes);
	if (hours >= 48) {
		return { value: roundToOneDecimal(hours / 24), unit: 'days' };
	}
	return { value: hours, unit: 'hours' };
};

const toPercentageOfDay = (minutes: number): number => {
	const hours = minutes / 60;
	const daysDecimal = hours / 24;
	return Math.round(daysDecimal * 100) / 100;
};

/**
 * Convert minutes to a time unit object showing fraction of 1 day.
 * Used for Team Workload "Tickets by Team Member" table.
 * Returns { value: 0.83, unit: 'days' }
 */
const toTimeUnitAsPercentageOfDay = (minutes: number): { value: number; unit: 'days' } => {
	return { value: toPercentageOfDay(minutes), unit: 'days' };
};

const PRIORITY_COLORS: Record<string, string> = {
	critical: '#D32F2F',
	high: '#F57C00',
	medium: '#1976D2',
	low: '#33ABDA',
	default: '#667085',
};

const normalizePriorityColor = (priority: string): string =>
	PRIORITY_COLORS[priority.trim().toLowerCase()] ?? PRIORITY_COLORS.default;

const getDayIndex = (cell: Record<string, unknown>): number => {
	const dayName = asString(cell.dayName).toLowerCase();
	if (dayName.startsWith('sun')) return 0;
	if (dayName.startsWith('mon')) return 1;
	if (dayName.startsWith('tue')) return 2;
	if (dayName.startsWith('wed')) return 3;
	if (dayName.startsWith('thu')) return 4;
	if (dayName.startsWith('fri')) return 5;
	if (dayName.startsWith('sat')) return 6;

	const dayOfWeek = asNumber(cell.dayOfWeek);
	if (dayOfWeek === 7) return 0;
	if (dayOfWeek >= 1 && dayOfWeek <= 6) return dayOfWeek;
	return -1;
};

const mapHeatmapMatrix = (heatmapPayload: unknown) => {
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const hours = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
	const matrix = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));

	const cells = Array.isArray(heatmapPayload)
		? asArray<Record<string, unknown>>(heatmapPayload)
		: asArray<Record<string, unknown>>(asObject(heatmapPayload)?.cells);

	cells.forEach((cell) => {
		const rowIndex = getDayIndex(cell);
		const hour = asNumber(cell.hourOfDay, -1);
		if (rowIndex < 0 || hour < 0 || hour > 23) {
			return;
		}

		matrix[rowIndex][hour] = asNumber(cell.ticketCount);
	});

	return { days, hours, matrix };
};

const buildCategoryId = (name: string): number => {
	let hash = 0;
	for (let index = 0; index < name.length; index += 1) {
		hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
	}
	return hash || 1;
};

const normalizeAgingLabel = (label: string): string => {
	const normalized = label.trim().toLowerCase();
	if (normalized === '0-4h' || normalized === '0 to 4 hours') return '0 to 4 hours';
	if (normalized === '4-24h' || normalized === '4 to 24 hours') return '4 to 24 hours';
	if (normalized === '1-3d' || normalized === '1 to 3 days') return '1 to 3 days';
	if (normalized === '3+d' || normalized === '3+ days') return '3+ days';
	return label;
};

/**
 * Normalizes ticket status names from backend to display format.
 * Maps "Waiting on IT" → "Escalated to IT" and "Waiting on TMV" → "Escalated to TMV"
 */
const normalizeStatusName = (status: string): string => {
	const normalized = status.trim().toLowerCase();
	if (normalized === 'waiting on it' || normalized === 'waiting-on-it') return 'Escalated to IT';
	if (normalized === 'waiting on tmv' || normalized === 'waiting-on-tmv') return 'Escalated to TMV';
	if (normalized === 'in progress') return 'In Progress';
	if (normalized === 'submitted to ado' || normalized === 'submitted-to-ado') return 'Submitted to ADO';
	if (normalized === 'waiting on user' || normalized === 'waiting-on-user') return 'Waiting on User';
	if (normalized === 'closed (merged)' || normalized === 'closed-merged') return 'Closed';
	// Return title case for known statuses
	return status.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

const metricTrend = (value: number, direction: 'up' | 'down', impact: 'positive' | 'negative' | null) => ({
	value,
	unit: 'percent' as const,
	direction,
	impact,
});

// ── Previous Period Comparison Logic ───────────────────────────────────────────

/**
 * Compute the previous period's filter params from the current timePeriodFilter.
 * Returns null for custom ranges (no comparison).
 */
const computePreviousPeriodFilter = (
	tpf: TicketingTimePeriodFilterPayload,
): { filter: string; value: string; year: string } | null => {
	if (tpf.filter === 'custom') return null;

	const year = Number(tpf.year);
	const value = String(tpf.value);

	switch (tpf.filter) {
		case 'day': {
			// Value is now YYYY-MM-DD format (standard ISO). Parse as year-month-day.
			const [y, m, d] = value.split('-');
			const date = new Date(Number(y), Number(m) - 1, Number(d));
			date.setDate(date.getDate() - 1);
			const prevYear = date.getFullYear();
			const prevMonth = `${date.getMonth() + 1}`.padStart(2, '0');
			const prevDay = `${date.getDate()}`.padStart(2, '0');
			return { filter: 'day', value: `${prevYear}-${prevMonth}-${prevDay}`, year: String(prevYear) };
		}
		case 'week': {
			const weekNum = Number(value);
			if (weekNum > 1) {
				return { filter: 'week', value: String(weekNum - 1), year: String(year) };
			}
			// Wrap to last week of previous year (52 weeks)
			return { filter: 'week', value: '52', year: String(year - 1) };
		}
		case 'period': {
			const periodNum = Number(value);
			if (periodNum > 1) {
				return { filter: 'period', value: String(periodNum - 1), year: String(year) };
			}
			// Wrap to last period of previous year (13 periods)
			return { filter: 'period', value: '13', year: String(year - 1) };
		}
		case 'quater': {
			const qNum = Number(value);
			if (qNum > 1) {
				return { filter: 'quater', value: String(qNum - 1), year: String(year) };
			}
			return { filter: 'quater', value: '4', year: String(year - 1) };
		}
		case 'year': {
			return { filter: 'year', value: String(year - 1), year: String(year - 1) };
		}
		default:
			return null;
	}
};

/**
 * Fetch the previous period's date range via fiscal-date-range API.
 * Returns null if the filter is custom or the API call fails.
 */
const fetchPreviousPeriodDates = async (
	tpf: TicketingTimePeriodFilterPayload,
): Promise<FiscalDateRangeResponse | null> => {
	const prev = computePreviousPeriodFilter(tpf);
	if (!prev) return null;

	try {
		return await fetchFiscalDateRange(prev.filter, prev.value, prev.year);
	} catch {
		console.warn('[TicketingAnalytics] Failed to fetch previous period dates');
		return null;
	}
};

/**
 * Calculate % change: ((current - previous) / previous) * 100
 * Returns null if previous is 0 (cannot divide).
 */
const calculatePercentChange = (current: number, previous: number): number | null => {
	if (previous === 0) return null;
	return roundToOneDecimal(((current - previous) / previous) * 100);
};

/**
 * Validate if a previous metric value is reasonable for trend calculation.
 * Prevents extreme % changes when previous period data is missing/near-zero.
 */
const isValidPreviousValue = (metricKey: string, prevVal: number): boolean => {
	// Minimum thresholds for each metric type
	const MIN_THRESHOLDS: Record<string, number> = {
		// Time-based metrics (in minutes): should be at least 5 minutes (realistic for ticket resolution)
		'average_resolution_time': 5,
		'average_time_new_in_progress': 5,
		// Count-based metrics: should be at least 1
		'total_tickets': 1,
		'total_closed': 1,
		'open_tickets': 1,
		'critical_tickets': 0.5,
		'duplicates_merged': 0.5,
		// Rate-based metrics (percent): should be at least 0.01% (very low but realistic)
		'it_escalation_rate': 0.01,
		'ado_escalation_rate': 0.01,
	};

	const minThreshold = MIN_THRESHOLDS[metricKey] ?? 0.1;
	if (prevVal < minThreshold) {
		console.warn(`[TicketingAnalytics] Previous value too low for ${metricKey}: ${prevVal} (min: ${minThreshold}) - skipping trend`);
		return false;
	}
	return true;
};

/**
 * Determines if a metric trend direction represents positive or negative impact.
 * 
 * Metrics where UP = BAD (negative impact):
 * - total_tickets: more tickets = increased workload (bad)
 * - open_tickets: more open = backlog growing (bad)
 * - critical_tickets: more critical = higher urgency (bad)
 * - average_resolution_time: longer time = slower resolution (bad)
 * - average_time_new_in_progress: longer wait time = inefficiency (bad)
 * - it_escalation_rate: higher rate = more escalations (bad)
 * - ado_escalation_rate: higher rate = more external dependencies (bad)
 * - duplicates_merged: more merged = more duplicates existed (neutral/negative)
 * 
 * Metrics where UP = GOOD (positive impact):
 * - total_closed: more closed = better throughput (good)
 * 
 * Metrics with no trend:
 * - open_access_requests: no trend comparison
 */
const getMetricTrendImpact = (metricKey: string, direction: 'up' | 'down'): 'positive' | 'negative' | null => {
	// Metrics where an increase is GOOD (positive impact)
	const positiveWhenUp = ['total_closed'];
	if (positiveWhenUp.includes(metricKey)) {
		return direction === 'up' ? 'positive' : 'negative';
	}

	// Metrics where an increase is BAD (negative impact)
	const negativeWhenUp = [
		'total_tickets',
		'open_tickets',
		'critical_tickets',
		'average_resolution_time',
		'average_time_new_in_progress',
		'it_escalation_rate',
		'ado_escalation_rate',
		'duplicates_merged',
	];
	if (negativeWhenUp.includes(metricKey)) {
		return direction === 'up' ? 'negative' : 'positive';
	}

	// Metrics with no trend or unknown metrics
	return null;
};

/** Metrics in the overview that should show period-over-period trends */
const METRICS_WITH_TREND = ['it_escalation_rate', 'ado_escalation_rate'];

interface OverviewRawValues {
	totalTickets: number;
	totalClosed: number;
	totalOpen: number;
	criticalOpen: number;
	avgResolutionMinutes: number;
	avgNewAndInProgressMinutes: number;
	itEscalationRatePct: number;
	adoEscalationRatePct: number;
	mergedCount: number;
}

/**
 * Extract raw metric values from API responses for comparison purposes.
 */
const extractOverviewRawValues = (
	overview: Record<string, unknown> | null,
	escalation: Record<string, unknown> | null,
	statusAvgTime: Record<string, unknown> | null,
	duplicateFlags: Record<string, unknown> | null,
): OverviewRawValues => {
	const avgTimesByStatus = new Map<string, number>();
	asArray<Record<string, unknown>>(statusAvgTime?.statuses).forEach((item) => {
		const rawStatus = asString(item.status);
		if (!rawStatus) {
			return;
		}
		const normalizedStatus = normalizeStatusName(rawStatus);
		avgTimesByStatus.set(normalizedStatus, asNumber(item.avgDurationMinutes));
	});

	const newMinutes = avgTimesByStatus.get('New') ?? 0;
	const inProgressMinutes = avgTimesByStatus.get('In Progress') ?? 0;

	return {
		totalTickets: asNumber(overview?.totalTickets ?? escalation?.totalTickets),
		totalClosed: asNumber(overview?.totalClosed),
		totalOpen: asNumber(overview?.totalOpen),
		criticalOpen: asNumber(overview?.criticalOpen),
		avgResolutionMinutes: asNumber(overview?.avgResolutionTimeMinutes),
		avgNewAndInProgressMinutes: newMinutes + inProgressMinutes,
		itEscalationRatePct: asNumber(overview?.itEscalationRatePct ?? escalation?.itRatePct),
		adoEscalationRatePct: asNumber(escalation?.adoRatePct),
		mergedCount: asNumber(duplicateFlags?.mergedCount),
	};
};

const getMetricRawValue = (key: string, values: OverviewRawValues): number | null => {
	switch (key) {
		case 'total_tickets': return values.totalTickets;
		case 'total_closed': return values.totalClosed;
		case 'open_tickets': return values.totalOpen;
		case 'critical_tickets': return values.criticalOpen;
		case 'average_resolution_time': return values.avgResolutionMinutes;
		case 'average_time_new_in_progress': return values.avgNewAndInProgressMinutes;
		case 'it_escalation_rate': return values.itEscalationRatePct;
		case 'ado_escalation_rate': return values.adoEscalationRatePct;
		case 'duplicates_merged': return values.mergedCount;
		default: return null;
	}
};

// ── End Previous Period Logic ─────────────────────────────────────────────────

/**
 * Builds the top 10 "overview" metric cards. Isolated as its own helper (and
 * its own fetch function below) so a failure in an unrelated section — e.g.
 * the Aging Tickets endpoint — can never prevent these cards from rendering.
 */
const buildOverviewMetrics = (
	semanticOverview: Record<string, unknown> | null,
	statusAvgTime: Record<string, unknown> | null,
	escalation: Record<string, unknown> | null,
	duplicateFlags: Record<string, unknown> | null,
	mergeStats: Record<string, unknown> | null,
	previousPeriodValues?: OverviewRawValues | null,
) => {
	const totalTickets = asNumber(semanticOverview?.totalTickets ?? escalation?.totalTickets);
	const totalClosed = asNumber(semanticOverview?.totalClosed);
	const totalOpen = asNumber(semanticOverview?.totalOpen);
	const criticalOpen = asNumber(semanticOverview?.criticalOpen);
	const avgResolutionMinutes = asNumber(semanticOverview?.avgResolutionTimeMinutes);
	const itEscalatedCount = asNumber(semanticOverview?.itEscalatedCount ?? escalation?.itCount);
	const itEscalationRatePct = asNumber(semanticOverview?.itEscalationRatePct ?? escalation?.itRatePct);
	const adoEscalatedCount = asNumber(escalation?.adoCount);
	const adoEscalationRatePct = asNumber(escalation?.adoRatePct);
	const mergedCountFromOverview = asNumber(semanticOverview?.mergedCount);
	const mergedCountFromFlags = asNumber(duplicateFlags?.mergedCount);
	const mergedCountFromMergeStats = asNumber(mergeStats?.totalMerged);
	const mergedCount = mergedCountFromFlags || mergedCountFromOverview || mergedCountFromMergeStats;

	const openAccessRequests = asNumber(semanticOverview?.openAccessRequests);

	const avgTimesByStatus = new Map<string, number>();
	asArray<Record<string, unknown>>(statusAvgTime?.statuses).forEach((item) => {
		const rawStatus = asString(item.status);
		if (!rawStatus) {
			return;
		}
		const normalizedStatus = normalizeStatusName(rawStatus);
		avgTimesByStatus.set(normalizedStatus, asNumber(item.avgDurationMinutes));
	});

	const newMinutes = avgTimesByStatus.get('New') ?? 0;
	const inProgressMinutes = avgTimesByStatus.get('In Progress') ?? 0;
	const avgNewAndInProgressHours = roundToOneDecimal((newMinutes + inProgressMinutes) / 120);

	return [
		{
			key: 'total_tickets',
			title: 'Total Tickets',
			value: totalTickets,
			unit: 'count',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('total_tickets', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('total_tickets', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(totalTickets, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('total_tickets', direction));
			})(),
			description: 'vs prior period',
		},
		{
			key: 'total_closed',
			title: 'Total Closed',
			value: totalClosed,
			unit: 'count',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('total_closed', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('total_closed', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(totalClosed, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('total_closed', direction));
			})(),
			description: `${safePercent(totalClosed, totalTickets).toFixed(1)}% of total tickets`,
		},
		{
			key: 'open_tickets',
			title: 'Open Tickets',
			value: totalOpen,
			unit: 'count',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('open_tickets', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('open_tickets', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(totalOpen, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('open_tickets', direction));
			})(),
			description: `${safePercent(totalOpen, totalTickets).toFixed(1)}% of total tickets`,
		},
		{
			key: 'critical_tickets',
			title: 'Critical Tickets',
			value: criticalOpen,
			unit: 'count',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('critical_tickets', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('critical_tickets', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(criticalOpen, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('critical_tickets', direction));
			})(),
			description: `${criticalOpen.toLocaleString()} currently open`,
		},
		{
			key: 'open_access_requests',
			title: 'Open Access Requests',
			value: openAccessRequests,
			unit: 'count',
			trend: null,
			description: 'IDM Separation queue',
		},
		{
			key: 'average_resolution_time',
			title: 'Average Resolution Time',
			value: roundToOneDecimal(avgResolutionMinutes / 60),
			unit: 'hours',
			trend: (() => {
				if (!previousPeriodValues) {
					// No previous period data available - show as baseline
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('average_resolution_time', previousPeriodValues);
				if (prevVal === null) {
					// Previous value null - show as baseline
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('average_resolution_time', prevVal)) {
					// Previous value too low/unrealistic - show as baseline instead of blocking
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(avgResolutionMinutes, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('average_resolution_time', direction));
			})(),
			description: 'Closed (Merged) excluded',
		},
		{
			key: 'average_time_new_in_progress',
			title: 'Average Time in New/In Progress',
			value: avgNewAndInProgressHours,
			unit: 'hours',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('average_time_new_in_progress', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('average_time_new_in_progress', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(newMinutes + inProgressMinutes, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('average_time_new_in_progress', direction));
			})(),
			description: 'Combined New + In Progress',
		},
		{
			key: 'it_escalation_rate',
			title: 'IT Escalation Rate',
			value: itEscalationRatePct,
			unit: 'percent',
			trend: (() => {
				if (!previousPeriodValues) return null;
				const prevVal = getMetricRawValue('it_escalation_rate', previousPeriodValues);
				if (prevVal === null) return null;
				const pctChange = calculatePercentChange(itEscalationRatePct, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('it_escalation_rate', direction));
			})(),
			description: `${itEscalatedCount.toLocaleString()} tickets, Resolution Path: Requires IT`,
		},
		{
			key: 'ado_escalation_rate',
			title: 'ADO Escalation Rate',
			value: adoEscalationRatePct,
			unit: 'percent',
			trend: (() => {
				if (!previousPeriodValues) return null;
				const prevVal = getMetricRawValue('ado_escalation_rate', previousPeriodValues);
				if (prevVal === null) return null;
				const pctChange = calculatePercentChange(adoEscalationRatePct, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('ado_escalation_rate', direction));
			})(),
			description: `${adoEscalatedCount.toLocaleString()} tickets reached Submitted to ADO`,
		},
		{
			key: 'duplicates_merged',
			title: 'Duplicates Merged',
			value: mergedCount,
			unit: 'count',
			trend: (() => {
				if (!previousPeriodValues) {
					return metricTrend(0, 'up' as const, null);
				}
				const prevVal = getMetricRawValue('duplicates_merged', previousPeriodValues);
				if (prevVal === null) {
					return metricTrend(0, 'up' as const, null);
				}
				if (!isValidPreviousValue('duplicates_merged', prevVal)) {
					return metricTrend(0, 'up' as const, null);
				}
				const pctChange = calculatePercentChange(mergedCount, prevVal);
				if (pctChange === null) return null;
				const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
				return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('duplicates_merged', direction));
			})(),
			description: `${safePercent(mergedCount, asNumber(duplicateFlags?.totalFlagged) || 1).toFixed(0)}% of potential duplicates`,
		},
	];
};

/** Builds the "Status Breakdown" cards. Isolated from Aging/Category/Duplicate sections. */
const buildStatusBreakdown = (
	statusDistribution: Record<string, unknown> | null,
	statusAvgTime: Record<string, unknown> | null,
	avgResolutionMinutes: number,
) => {
	const avgTimesByStatus = new Map<string, number>();
	asArray<Record<string, unknown>>(statusAvgTime?.statuses).forEach((item) => {
		const rawStatus = asString(item.status);
		if (!rawStatus) {
			return;
		}
		const normalizedStatus = normalizeStatusName(rawStatus);
		avgTimesByStatus.set(normalizedStatus, asNumber(item.avgDurationMinutes));
	});

	const statusCounts = new Map<string, number>();
	asArray<Record<string, unknown>>(statusDistribution?.statuses).forEach((item) => {
		const rawStatus = asString(item.status);
		if (!rawStatus) {
			return;
		}
		const normalizedStatus = normalizeStatusName(rawStatus);
		statusCounts.set(normalizedStatus, asNumber(item.count));
	});

	const statusOrder = [
		'New',
		'In Progress',
		'Escalated to IT',
		'Escalated to TMV',
		'Submitted to ADO',
		'Waiting on User',
		'Closed',
	];

	return statusOrder.map((status) => {
		const avgMinutes = avgTimesByStatus.get(status);
		return {
			status,
			count: statusCounts.get(status) ?? 0,
			description: avgMinutes
				? `Avg time in status: ${humanizeDuration(avgMinutes)}`
				: status === 'Closed'
					? `Avg resolution: ${humanizeDuration(avgResolutionMinutes)}`
					: 'Avg time in status: —',
		};
	});
};

/**
 * Builds the "Aging Tickets (Open)" cards from the aging-buckets response.
 * This is the only helper that depends on SEMANTIC_AGING_ENDPOINT, so a
 * failure of that endpoint can never bleed into any other section.
 */
const buildAgingTickets = (aging: Record<string, unknown> | null) => {
	const agingCountsByBucket = new Map<string, number>();
	asArray<Record<string, unknown>>(aging?.buckets).forEach((bucket) => {
		const normalizedLabel = normalizeAgingLabel(asString(bucket.label));
		if (!normalizedLabel) {
			return;
		}
		agingCountsByBucket.set(normalizedLabel, asNumber(bucket.count));
	});

	const agingOrder = ['0 to 4 hours', '4 to 24 hours', '1 to 3 days', '3+ days'];
	return agingOrder.map((bucket) => ({
		bucket,
		count: agingCountsByBucket.get(bucket) ?? 0,
		description: bucket === '3+ days' ? 'Stale: no status change in 3+ days' : 'Open tickets in bucket',
	}));
};

/** Builds the "Total Tickets by Category" bar-chart rows (total + open views). */
const buildCategoryBreakdown = (
	categoryBreakdown: Record<string, unknown> | null,
	lookup?: CategoryLookup,
) => {
	const categoryTotals = new Map<string, { total: number; open: number }>();
	asArray<Record<string, unknown>>(categoryBreakdown?.categories).forEach((item) => {
		// Resolve numeric categoryId → human-readable name
		const categoryName = resolveName(item.categoryId, lookup?.categoryIdToName);
		if (!categoryName) {
			return;
		}

		const previous = categoryTotals.get(categoryName) ?? { total: 0, open: 0 };
		categoryTotals.set(categoryName, {
			total: previous.total + asNumber(item.totalCount),
			open: previous.open + asNumber(item.openCount),
		});
	});

	const toCategoryRows = (view: 'total' | 'open') => {
		const totals = Array.from(categoryTotals.entries()).map(([name, counts]) => ({
			name,
			count: view === 'total' ? counts.total : counts.open,
		}));

		totals.sort((a, b) => b.count - a.count);
		const denominator = totals.reduce((sum, item) => sum + item.count, 0);

		return totals.map((item) => ({
			name: item.name,
			count: item.count,
			percentage: safePercent(item.count, denominator),
		}));
	};

	return [
		{ view: 'total', categories: toCategoryRows('total') },
		{ view: 'open', categories: toCategoryRows('open') },
	];
};

/** Builds the "Duplicate Activity" cards. */
const buildDuplicateActivity = (duplicateFlags: Record<string, unknown> | null) => {
	const potentialDuplicates = asNumber(duplicateFlags?.totalFlagged);
	const mergedDuplicates = asNumber(duplicateFlags?.mergedCount);
	const falsePositives = asNumber(duplicateFlags?.falsePositiveCount);
	const mergedPct = asNumber(duplicateFlags?.mergedPct);
	const falsePositivePct = asNumber(duplicateFlags?.falsePositivePct);
	const avgBetweenMinutes = asNumber(duplicateFlags?.avgTimeBetweenDuplicateMinutes);

	return [
		{
			metric: 'Potential Duplicates',
			count: potentialDuplicates,
			percentage: null,
			description: 'Same Category and Sub-Category, open, within 24 hours',
		},
		{
			metric: 'Merged Tickets',
			count: mergedDuplicates,
			percentage: mergedPct,
			description: 'of potential duplicates',
		},
		{
			metric: 'False Positives',
			count: falsePositives,
			percentage: falsePositivePct,
			description: 'Aged out or closed without merge',
		},
		{
			metric: 'Avg Time Between Dupes',
			value: roundToOneDecimal(avgBetweenMinutes / 60),
			unit: 'hours',
			description: 'Between merged duplicate pairs',
		},
	];
};

const mapOverviewFromSemanticLayer = (
	semanticOverview: Record<string, unknown> | null,
	aging: Record<string, unknown> | null,
	statusDistribution: Record<string, unknown> | null,
	statusAvgTime: Record<string, unknown> | null,
	escalation: Record<string, unknown> | null,
	duplicateFlags: Record<string, unknown> | null,
	mergeStats: Record<string, unknown> | null,
	categoryBreakdown: Record<string, unknown> | null,
	lookup?: CategoryLookup,
	previousPeriodValues?: OverviewRawValues | null,
): TicketingOverviewPayload => {
	const avgResolutionMinutes = asNumber(semanticOverview?.avgResolutionTimeMinutes);

	return {
		overview: buildOverviewMetrics(semanticOverview, statusAvgTime, escalation, duplicateFlags, mergeStats, previousPeriodValues),
		statusBreakdown: buildStatusBreakdown(statusDistribution, statusAvgTime, avgResolutionMinutes),
		agingTickets: buildAgingTickets(aging),
		ticketsByCategory: buildCategoryBreakdown(categoryBreakdown, lookup),
		duplicateActivity: buildDuplicateActivity(duplicateFlags),
	};
};

/**
 * Fetches the previous fiscal period's raw metric values for trend comparison.
 * Best-effort only: any failure here silently disables trends rather than
 * failing the caller, since trends are a secondary enhancement.
 */
const resolvePreviousPeriodValues = async (
	request: TicketingOverviewRequest,
	body: TicketingAnalyticsSemanticFilter,
): Promise<OverviewRawValues | null> => {
	const tpf = request.timePeriodFilter;
	if (!tpf || tpf.filter === 'custom') {
		return null;
	}

	try {
		const prevDates = await fetchPreviousPeriodDates(tpf);
		if (!prevDates) {
			return null;
		}

		const prevBody: TicketingAnalyticsSemanticFilter = {
			...body,
			fromDate: prevDates.fromDate,
			toDate: prevDates.toDate,
		};
		const [prevOverview, prevEscalation, prevStatusAvgTime, prevDuplicateFlags] = await Promise.all([
			postSemantic(SEMANTIC_OVERVIEW_ENDPOINT, prevBody),
			postSemantic(SEMANTIC_ESCALATION_ENDPOINT, prevBody),
			postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, prevBody),
			postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, prevBody),
		]);
		return extractOverviewRawValues(prevOverview, prevEscalation, prevStatusAvgTime, prevDuplicateFlags);
	} catch (err) {
		console.warn('[TicketingAnalytics] Previous period comparison failed:', err);
		return null;
	}
};

const fetchSemanticOverview = async (request: TicketingOverviewRequest, lookup?: CategoryLookup): Promise<TicketingOverviewPayload> => {
	const body = await buildSemanticFilterBody(request);

	const [
		overview,
		aging,
		statusDistribution,
		statusAvgTime,
		escalation,
		duplicateFlags,
		mergeStats,
		categoryBreakdown,
	] = await Promise.all([
		postSemantic(SEMANTIC_OVERVIEW_ENDPOINT, body),
		postSemantic(SEMANTIC_AGING_ENDPOINT, body),
		postSemantic(SEMANTIC_STATUS_DISTRIBUTION_ENDPOINT, body),
		postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, body),
		postSemantic(SEMANTIC_ESCALATION_ENDPOINT, body),
		postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, body),
		postSemantic(SEMANTIC_MERGE_STATS_ENDPOINT, body),
		postSemantic(SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT, body),
	]);

	if (!overview && !statusDistribution && !aging) {
		throw new Error('Ticketing Analytics semantic-layer overview endpoints did not return data.');
	}

	const previousPeriodValues = await resolvePreviousPeriodValues(request, body);

	return mapOverviewFromSemanticLayer(
		overview,
		aging,
		statusDistribution,
		statusAvgTime,
		escalation,
		duplicateFlags,
		mergeStats,
		categoryBreakdown,
		lookup,
		previousPeriodValues,
	);
};

// ─── Independent per-section fetchers (Overview tab) ───────────────────────
// Each function below owns exactly the endpoint(s) needed for one Overview
// card/section. This lets the Dashboard give every card its own
// loading/error/retry lifecycle: a failure in one (e.g. Aging Tickets, whose
// SEMANTIC_AGING_ENDPOINT is the documented failure test case) never affects
// the others, and Retry only re-calls that section's own endpoint(s).

/** "Overview" top metric cards (Total Tickets, Total Closed, etc.). */
export const fetchTicketingOverviewCoreMetrics = async (
	request: TicketingOverviewRequest,
): Promise<TicketingOverviewPayload['overview']> => {
	const body = await buildSemanticFilterBody(request);
	const [overview, escalation, statusAvgTime, duplicateFlags, mergeStats] = await Promise.all([
		postSemantic(SEMANTIC_OVERVIEW_ENDPOINT, body),
		postSemantic(SEMANTIC_ESCALATION_ENDPOINT, body),
		postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, body),
		postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, body),
		postSemantic(SEMANTIC_MERGE_STATS_ENDPOINT, body),
	]);
	const previousPeriodValues = await resolvePreviousPeriodValues(request, body);
	return buildOverviewMetrics(overview, statusAvgTime, escalation, duplicateFlags, mergeStats, previousPeriodValues);
};

/** "Status Breakdown" cards. */
export const fetchTicketingOverviewStatusBreakdown = async (
	request: TicketingOverviewRequest,
): Promise<TicketingOverviewPayload['statusBreakdown']> => {
	const body = await buildSemanticFilterBody(request);
	const [statusDistribution, statusAvgTime, overview] = await Promise.all([
		postSemantic(SEMANTIC_STATUS_DISTRIBUTION_ENDPOINT, body),
		postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, body),
		postSemantic(SEMANTIC_OVERVIEW_ENDPOINT, body),
	]);
	const avgResolutionMinutes = asNumber(overview?.avgResolutionTimeMinutes);
	return buildStatusBreakdown(statusDistribution, statusAvgTime, avgResolutionMinutes);
};

/**
 * "Aging Tickets (Open)" cards — the isolated test case for the intentionally
 * failing SEMANTIC_AGING_ENDPOINT. This is the only fetcher that calls it.
 */
export const fetchTicketingOverviewAgingTickets = async (
	request: TicketingOverviewRequest,
): Promise<TicketingOverviewPayload['agingTickets']> => {
	const body = await buildSemanticFilterBody(request);
	const aging = await postSemantic(SEMANTIC_AGING_ENDPOINT, body);
	return buildAgingTickets(aging);
};

/** "Total Tickets by Category" bar chart. */
export const fetchTicketingOverviewCategoryBreakdown = async (
	request: TicketingOverviewRequest,
	lookup?: CategoryLookup,
): Promise<TicketingOverviewPayload['ticketsByCategory']> => {
	const body = await buildSemanticFilterBody(request);
	const categoryBreakdown = await postSemantic(SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT, body);
	return buildCategoryBreakdown(categoryBreakdown, lookup);
};

/** "Duplicate Activity" cards. */
export const fetchTicketingOverviewDuplicateActivity = async (
	request: TicketingOverviewRequest,
): Promise<TicketingOverviewPayload['duplicateActivity']> => {
	const body = await buildSemanticFilterBody(request);
	const duplicateFlags = await postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, body);
	return buildDuplicateActivity(duplicateFlags);
};

const fetchSemanticVolumeTrends = async (
	request: TicketingVolumeTrendsRequest,
): Promise<TicketingVolumeTrendsPayload> => {
	const body = await buildSemanticFilterBody(request);
	const volume = await postSemanticWithParams(SEMANTIC_VOLUME_ENDPOINT, body, { useWeeklyTrailing: 'true' });

	let priorities = asArray<Record<string, unknown>>(volume?.ticketsByPriority);
	let resolutionByPriority = asArray<Record<string, unknown>>(volume?.resolutionTimeByPriority);
	let submissionHeatMapPayload: unknown = volume?.submissionHeatMap;
	let resolutionHeatMapPayload: unknown = volume?.resolutionHeatMap;

	const buckets = asArray<Record<string, unknown>>(volume?.buckets);
	if (!resolutionByPriority.length) {
		resolutionByPriority = priorities;
	}

	if (!buckets.length && !priorities.length) {
		// Backend returned 0 data for this filter — return empty payload.
		return {
			weeklyTicketVolume: { title: 'Weekly Ticket Volume', series: [] },
			ticketsByPriority: {
				selectedView: 'total' as const,
				total: { totalTickets: 0, segments: [] },
				open: { totalTickets: 0, segments: [] },
			},
			resolutionTimeByPriority: [],
			submissionHeatMap: mapHeatmapMatrix([]),
			resolutionHeatMap: mapHeatmapMatrix([]),
		};
	}

	const totalFromPriority = priorities.reduce((sum, item) => sum + asNumber(item.totalCount), 0);
	const totalTickets = totalFromPriority || asNumber(volume?.totalTickets);
	const openTickets = priorities.reduce((sum, item) => sum + asNumber(item.openCount), 0);
	const toSegments = (countKey: 'totalCount' | 'openCount', denominator: number) =>
		priorities.map((item) => {
			const name = asString(item.urgency, 'Unknown');
			const value = asNumber(item[countKey]);
			return {
				name,
				value,
				percentage: safePercent(value, denominator),
				color: normalizePriorityColor(name),
			};
		});

	return {
		weeklyTicketVolume: {
			title: 'Weekly Ticket Volume',
			series: buckets.map((bucket) => ({
				label: asString(bucket.label),
				value: asNumber(bucket.count),
			})),
		},
		ticketsByPriority: {
			selectedView: 'total',
			total: {
				totalTickets,
				segments: toSegments('totalCount', totalTickets),
			},
			open: {
				totalTickets: openTickets,
				segments: toSegments('openCount', openTickets),
			},
		},
		resolutionTimeByPriority: (() => {
			const displayOrder = ['critical', 'high', 'medium', 'low'];
			return [...resolutionByPriority].sort((a, b) => {
				const aIndex = displayOrder.indexOf(asString(a.urgency, 'Unknown').toLowerCase());
				const bIndex = displayOrder.indexOf(asString(b.urgency, 'Unknown').toLowerCase());
				return (
					(aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
					(bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
				);
			}).map((item) => {
				const name = asString(item.urgency, 'Unknown');
				return {
					name,
					value: toHours(asNumber(item.avgResolutionMinutes)),
					unit: 'hours',
					color: normalizePriorityColor(name),
				};
			});
		})(),
		submissionHeatMap: mapHeatmapMatrix(submissionHeatMapPayload),
		resolutionHeatMap: mapHeatmapMatrix(resolutionHeatMapPayload),
	};
};

const fetchSemanticLifecycle = async (
	request: TicketingLifecycleRequest,
	lookup?: CategoryLookup,
): Promise<TicketingLifecyclePayload> => {
	const body = await buildSemanticFilterBody(request);
	const [cycleTimes, statusAvgTime, escalation, adoConversion, staleTickets, categoryBreakdown, duplicateFlags] = await Promise.all([
		postSemantic(SEMANTIC_CYCLE_TIMES_ENDPOINT, body),
		postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, body),
		postSemantic(SEMANTIC_ESCALATION_ENDPOINT, body),
		postSemantic(SEMANTIC_ADO_CONVERSION_RATE_ENDPOINT, body),
		postSemanticWithParams(SEMANTIC_STALE_TICKETS_ENDPOINT, body, { page: 0, pageSize: 100, sortBy: 'age', sortOrder: 'desc' }),
		postSemantic(SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT, body),
		postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, body),
	])

	const statuses = asArray<Record<string, unknown>>(statusAvgTime?.statuses);
	if (!statuses.length && !asObject(cycleTimes?.summary)) {
		return {
			overview: [],
			resolutionTimeByStatus: {
				title: 'Resolution Time by Status',
				selectedView: 'all',
				all: { series: [] },
				systemIssue: { series: [] },
				accessRequest: { series: [] },
				reporting: { series: [] },
				training: { series: [] },
				systemEnhancement: { series: [] },
				hardware: { series: [] },
			},
			resolvingParty: { title: 'Resolving Party', totalResolved: 0, series: [] },
			staleTickets: {
				title: 'Stale Tickets',
				description: '',
				tickets: [],
				totalCount: 0,
				page: 0,
				pageSize: 50,
				hasNextPage: false,
				hasPreviousPage: false,
				executionTimeMs: 0,
			},
			resolutionTimePerCategory: { title: 'Resolution Time per Category', selectedCategory: null, categories: [] },
		};
	}

	const cycleSummary = asObject(cycleTimes?.summary);

	// Bucket-over-bucket trend for assignment time, using the time series the
	// cycle-times endpoint already returns — no extra request needed.
	// Sorted defensively rather than trusting API ordering; the `bucket` field is
	// zero-padded (2026-07, 2026-W32, 2026-08-07) so it sorts lexicographically
	// for every granularity. .slice() keeps us from mutating the parsed response.
	const trendBuckets = asArray<Record<string, unknown>>(cycleTimes?.trend)
		.slice()
		.sort((left, right) => asString(left.bucket).localeCompare(asString(right.bucket)));

	const assignmentTrend = (() => {
		// Fewer than two buckets means there is nothing to compare against.
		if (trendBuckets.length < 2) {
			return null;
		}

		const current = asNumber(trendBuckets[trendBuckets.length - 1]?.avgAssignmentTimeMinutes);
		const previous = asNumber(trendBuckets[trendBuckets.length - 2]?.avgAssignmentTimeMinutes);
		const pctChange = calculatePercentChange(current, previous);
		// null when previous is 0 — a percentage change is undefined there.
		if (pctChange === null) {
			return null;
		}

		const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
		return metricTrend(
			Math.abs(pctChange),
			direction,
			getMetricTrendImpact('average_assignment_time', direction),
		);
	})();
	const itRatePct = asNumber(escalation?.itRatePct);
	const tmvRatePct = asNumber(escalation?.tmvRatePct);
	// Real single-path count from the escalation endpoint — read directly rather than
	// derived from tmvRatePct, which would only recompute it from a rounded percentage.
	const tmvEscalatedCount = asNumber(escalation?.tmvCount);
	const adoRatePct = asNumber(escalation?.adoRatePct);
	const combinedRatePct = asNumber(escalation?.combinedRatePct);
	const conversionRatePct = asNumber(adoConversion?.conversionRatePct);
	const potentialDupCount = asNumber(duplicateFlags?.totalFlagged);
	// Use the backend's own rate rather than recomputing from totalTicketsInScope:
	// duplicate-flags scopes its denominator more narrowly than all tickets in range,
	// so a local calculation would disagree with the source of truth.
	const duplicateRatePct = asNumber(duplicateFlags?.duplicateRatePct);
	// Total tickets in scope for the applied filters — the denominator behind the
	// escalation rates, used below to recover the combined escalated count.
	const totalTicketsInScope = asNumber(escalation?.totalTickets);
	// Derived from combinedRatePct rather than summing itCount + tmvCount + adoCount:
	// the backend de-duplicates the combined rate, whereas those counts would
	// double-count a ticket that passed through more than one escalation path.
	const combinedEscalatedCount = Math.round((combinedRatePct / 100) * totalTicketsInScope);


	const overview = [
		{
			key: 'average_assignment_time',
			title: 'Average Assignment Time',
			value: toHours(asNumber(cycleSummary?.avgAssignmentTimeMinutes)),
			unit: 'hours',
			trend: assignmentTrend,
			description: 'Submission to first assignment',
		},
		{
			key: 'total_escalation_rate',
			title: 'Total Escalation Rate',
			value: combinedRatePct,
			unit: 'percent',
			trend: null,
			description: `${combinedEscalatedCount.toLocaleString()} tickets entered IT, TMV, or ADO escalation`,
		},
		{
			key: 'ado_escalation_rate',
			title: 'ADO Escalation Rate',
			value: adoRatePct,
			unit: 'percent',
			trend: null,
			description: `${conversionRatePct.toFixed(1)}% conversion of ADO-eligible tickets`,
		},
		{
			key: 'tmv_escalation_rate',
			title: 'TMV Escalation Rate',
			value: tmvRatePct,
			unit: 'percent',
			trend: null,
			description: `${tmvEscalatedCount.toLocaleString()} tickets escalated to TMV`,
		},
		{
			key: 'it_escalation_rate',
			title: 'IT Escalation Rate',
			value: itRatePct,
			unit: 'percent',
			trend: null,
			description: 'Resolution Path: Requires IT',
		},
		{
			key: 'potential_duplicates_rate',
			title: 'Potential Duplicates Rate',
			value: roundToOneDecimal(duplicateRatePct),
			unit: 'percent',
			trend: null,
			description: `${potentialDupCount.toLocaleString()} tickets flagged as possible duplicates`,
		},
	];

	const resolutionTimeByStatusSeries = statuses.map((item) => {
		const display = toDisplayTimeValue(asNumber(item.avgDurationMinutes));
		return {
			status: asString(item.status),
			value: display.value,
			unit: display.unit,
		};
	});

	const totalResolved = asNumber(cycleSummary?.sampleSize);
	const resolvingCounts = [
		{ name: 'Navi Team', count: tmvEscalatedCount },
		{ name: 'WES Team', count: asNumber(escalation?.adoCount) },
		{ name: 'IT Team', count: asNumber(escalation?.itCount) },
	];
	const nonSustain = resolvingCounts.reduce((sum, item) => sum + item.count, 0);
	const sustainCount = Math.max(0, totalResolved - nonSustain);
	const resolvingSeries = [
		{ name: 'Sustain Team', count: sustainCount },
		...resolvingCounts,
	].map((item) => ({
		name: item.name,
		count: item.count,
		percentage: safePercent(item.count, totalResolved || item.count),
	}));

	const staleRows = asArray<Record<string, unknown>>(staleTickets?.tickets).map((ticket) => ({
		ticketNumber: asString(ticket.ticketNumber),
		ticketTitle: asString(ticket.ticketTitle, asString(ticket.summary, '')),
		category: asString(ticket.categoryName, asString(ticket.category, '')),
		categoryName: asString(ticket.categoryName, ''),
		subCategory: resolveName(ticket.subCategoryId, lookup?.subCategoryIdToName, asString(ticket.subCategoryName, asString(ticket.subCategory, ''))),
		subCategoryId: asNumber(ticket.subCategoryId),
		subCategoryName: resolveName(ticket.subCategoryId, lookup?.subCategoryIdToName, asString(ticket.subCategoryName, '')),
		urgency: asString(ticket.urgency),
		currentStatus: asString(ticket.currentStatus),
		age: asString(ticket.age, `${roundToWhole(asNumber(ticket.minutesSinceLastChange) / 60)}h`),
		submitted: asString(ticket.createdAt, asString(ticket.submitted, '')),
		assignedTo: asString(ticket.currentAssignee, asString(ticket.assignedTo, 'Unassigned')),
	}));

	const categoryRows = new Map<string, { totalMinutes: number; weight: number; sub: Map<string, { totalMinutes: number; weight: number }> }>();
	asArray<Record<string, unknown>>(categoryBreakdown?.categories).forEach((row) => {
		const categoryName = resolveName(row.categoryId, lookup?.categoryIdToName);
		if (!categoryName) {
			return;
		}

		const subCategoryName = resolveName(row.subCategoryId, lookup?.subCategoryIdToName);
		const weight = asNumber(row.closedCount) || asNumber(row.totalCount) || 1;
		const avgResolutionMinutes = asNumber(row.avgResolutionMinutes);

		const categoryEntry = categoryRows.get(categoryName) ?? {
			totalMinutes: 0,
			weight: 0,
			sub: new Map(),
		};
		categoryEntry.totalMinutes += avgResolutionMinutes * weight;
		categoryEntry.weight += weight;

		if (subCategoryName) {
			const subEntry = categoryEntry.sub.get(subCategoryName) ?? {
				totalMinutes: 0,
				weight: 0,
			};
			subEntry.totalMinutes += avgResolutionMinutes * weight;
			subEntry.weight += weight;
			categoryEntry.sub.set(subCategoryName, subEntry);
		}

		categoryRows.set(categoryName, categoryEntry);
	});

	const resolutionTimePerCategory = {
		title: 'Resolution Time Per Category',
		selectedCategory: null,
		categories: Array.from(categoryRows.entries()).map(([category, value]) => {
			const avgMinutes = value.weight ? value.totalMinutes / value.weight : 0;
			const categoryDisplay = toDisplayTimeValue(avgMinutes);
			return {
				category,
				value: categoryDisplay.value,
				unit: categoryDisplay.unit,
				subCategories: Array.from(value.sub.entries()).map(([name, subValue]) => {
					const subAvgMinutes = subValue.weight ? subValue.totalMinutes / subValue.weight : 0;
					const subDisplay = toDisplayTimeValue(subAvgMinutes);
					return {
						name,
						value: subDisplay.value,
						unit: subDisplay.unit,
					};
				}),
			};
		}),
	};

	return {
		overview,
		resolutionTimeByStatus: {
			title: 'Resolution Time by Status',
			selectedView: 'all',
			all: { series: resolutionTimeByStatusSeries },
			systemIssue: { series: [] },
			accessRequest: { series: [] },
			reporting: { series: [] },
			training: { series: [] },
			systemEnhancement: { series: [] },
			hardware: { series: [] },
		},
		resolvingParty: {
			title: 'Resolving Party',
			totalResolved,
			series: resolvingSeries,
		},
		staleTickets: {
			title: 'Stale Tickets',
			description: 'Open tickets with no status change in 3+ days, oldest first.',
			tickets: staleRows,
			totalCount: asNumber(staleTickets?.totalCount ?? 0),
			page: asNumber(staleTickets?.page ?? 0),
			pageSize: asNumber(staleTickets?.pageSize ?? 50),
			hasNextPage: Boolean(staleTickets?.hasNextPage),
			hasPreviousPage: Boolean(staleTickets?.hasPreviousPage),
			executionTimeMs: asNumber(staleTickets?.executionTimeMs ?? 0),
		},
		resolutionTimePerCategory,
	};
};

// ─── Independent per-section fetchers (Lifecycle tab) ──────────────────────
// Mirrors the Overview tab pattern: each section calls only the endpoint(s)
// it needs so one failing endpoint (e.g. Stale Tickets) never blanks the
// other cards on the tab.

/** "Overview" metric cards (Average Assignment Time, escalation rates, etc.). */
export const fetchTicketingLifecycleOverview = async (
	request: TicketingLifecycleRequest,
): Promise<TicketingLifecyclePayload['overview']> => {
	const body = await buildSemanticFilterBody(request);
	const [cycleTimes, escalation, adoConversion, duplicateFlags] = await Promise.all([
		postSemantic(SEMANTIC_CYCLE_TIMES_ENDPOINT, body),
		postSemantic(SEMANTIC_ESCALATION_ENDPOINT, body),
		postSemantic(SEMANTIC_ADO_CONVERSION_RATE_ENDPOINT, body),
		postSemantic(SEMANTIC_DUPLICATE_FLAGS_ENDPOINT, body),
	]);

	const cycleSummary = asObject(cycleTimes?.summary);
	const trendBuckets = asArray<Record<string, unknown>>(cycleTimes?.trend)
		.slice()
		.sort((left, right) => asString(left.bucket).localeCompare(asString(right.bucket)));

	const assignmentTrend = (() => {
		if (trendBuckets.length < 2) {
			return null;
		}
		const current = asNumber(trendBuckets[trendBuckets.length - 1]?.avgAssignmentTimeMinutes);
		const previous = asNumber(trendBuckets[trendBuckets.length - 2]?.avgAssignmentTimeMinutes);
		const pctChange = calculatePercentChange(current, previous);
		if (pctChange === null) {
			return null;
		}
		const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
		return metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('average_assignment_time', direction));
	})();

	const itRatePct = asNumber(escalation?.itRatePct);
	const tmvRatePct = asNumber(escalation?.tmvRatePct);
	const tmvEscalatedCount = asNumber(escalation?.tmvCount);
	const adoRatePct = asNumber(escalation?.adoRatePct);
	const combinedRatePct = asNumber(escalation?.combinedRatePct);
	const conversionRatePct = asNumber(adoConversion?.conversionRatePct);
	const potentialDupCount = asNumber(duplicateFlags?.totalFlagged);
	const duplicateRatePct = asNumber(duplicateFlags?.duplicateRatePct);
	const totalTicketsInScope = asNumber(escalation?.totalTickets);
	const combinedEscalatedCount = Math.round((combinedRatePct / 100) * totalTicketsInScope);

	return [
		{
			key: 'average_assignment_time',
			title: 'Average Assignment Time',
			value: toHours(asNumber(cycleSummary?.avgAssignmentTimeMinutes)),
			unit: 'hours',
			trend: assignmentTrend,
			description: 'Submission to first assignment',
		},
		{
			key: 'total_escalation_rate',
			title: 'Total Escalation Rate',
			value: combinedRatePct,
			unit: 'percent',
			trend: null,
			description: `${combinedEscalatedCount.toLocaleString()} tickets entered IT, TMV, or ADO escalation`,
		},
		{
			key: 'ado_escalation_rate',
			title: 'ADO Escalation Rate',
			value: adoRatePct,
			unit: 'percent',
			trend: null,
			description: `${conversionRatePct.toFixed(1)}% conversion of ADO-eligible tickets`,
		},
		{
			key: 'tmv_escalation_rate',
			title: 'TMV Escalation Rate',
			value: tmvRatePct,
			unit: 'percent',
			trend: null,
			description: `${tmvEscalatedCount.toLocaleString()} tickets escalated to TMV`,
		},
		{
			key: 'it_escalation_rate',
			title: 'IT Escalation Rate',
			value: itRatePct,
			unit: 'percent',
			trend: null,
			description: 'Resolution Path: Requires IT',
		},
		{
			key: 'potential_duplicates_rate',
			title: 'Potential Duplicates Rate',
			value: roundToOneDecimal(duplicateRatePct),
			unit: 'percent',
			trend: null,
			description: `${potentialDupCount.toLocaleString()} tickets flagged as possible duplicates`,
		},
	];
};

/** "Resolving Party" stacked bar. */
export const fetchTicketingLifecycleResolvingParty = async (
	request: TicketingLifecycleRequest,
): Promise<TicketingLifecyclePayload['resolvingParty']> => {
	const body = await buildSemanticFilterBody(request);
	const [cycleTimes, escalation] = await Promise.all([
		postSemantic(SEMANTIC_CYCLE_TIMES_ENDPOINT, body),
		postSemantic(SEMANTIC_ESCALATION_ENDPOINT, body),
	]);

	const cycleSummary = asObject(cycleTimes?.summary);
	const totalResolved = asNumber(cycleSummary?.sampleSize);
	const tmvEscalatedCount = asNumber(escalation?.tmvCount);
	const resolvingCounts = [
		{ name: 'Navi Team', count: tmvEscalatedCount },
		{ name: 'WES Team', count: asNumber(escalation?.adoCount) },
		{ name: 'IT Team', count: asNumber(escalation?.itCount) },
	];
	const nonSustain = resolvingCounts.reduce((sum, item) => sum + item.count, 0);
	const sustainCount = Math.max(0, totalResolved - nonSustain);
	const resolvingSeries = [
		{ name: 'Sustain Team', count: sustainCount },
		...resolvingCounts,
	].map((item) => ({
		name: item.name,
		count: item.count,
		percentage: safePercent(item.count, totalResolved || item.count),
	}));

	return {
		title: 'Resolving Party',
		totalResolved,
		series: resolvingSeries,
	};
};

/** "Stale Tickets" table — the only fetcher that calls SEMANTIC_STALE_TICKETS_ENDPOINT. */
export const fetchTicketingLifecycleStaleTickets = async (
	request: TicketingLifecycleRequest,
	lookup?: CategoryLookup,
): Promise<TicketingLifecyclePayload['staleTickets']> => {
	const body = await buildSemanticFilterBody(request);
	// Fetch all stale tickets at once with large pageSize (10000) to avoid pagination
	const staleTickets = await postSemanticWithParams(SEMANTIC_STALE_TICKETS_ENDPOINT, body, { page: 0, pageSize: 10000, sortBy: 'age', sortOrder: 'desc' });

	const staleRows = asArray<Record<string, unknown>>(staleTickets?.tickets).map((ticket) => ({
		ticketNumber: asString(ticket.ticketNumber),
		ticketTitle: asString(ticket.ticketTitle, asString(ticket.summary, '')),
		category: asString(ticket.categoryName, asString(ticket.category, '')),
		categoryName: asString(ticket.categoryName, ''),
		subCategory: resolveName(ticket.subCategoryId, lookup?.subCategoryIdToName, asString(ticket.subCategoryName, asString(ticket.subCategory, ''))),
		subCategoryId: asNumber(ticket.subCategoryId),
		subCategoryName: resolveName(ticket.subCategoryId, lookup?.subCategoryIdToName, asString(ticket.subCategoryName, '')),
		urgency: asString(ticket.urgency),
		currentStatus: asString(ticket.currentStatus),
		age: asString(ticket.age, `${roundToWhole(asNumber(ticket.minutesSinceLastChange) / 60)}h`),
		submitted: asString(ticket.createdAt, asString(ticket.submitted, '')),
		assignedTo: asString(ticket.currentAssignee, asString(ticket.assignedTo, 'Unassigned')),
	}));

	return {
		title: 'Stale Tickets',
		description: 'Open tickets with no status change in 3+ days, oldest first.',
		tickets: staleRows,
		totalCount: asNumber(staleTickets?.totalCount ?? 0),
		page: asNumber(staleTickets?.page ?? 0),
		pageSize: asNumber(staleTickets?.pageSize ?? 50),
		hasNextPage: Boolean(staleTickets?.hasNextPage),
		hasPreviousPage: Boolean(staleTickets?.hasPreviousPage),
		executionTimeMs: asNumber(staleTickets?.executionTimeMs ?? 0),
	};
};

/** "Resolution Time per Category" bar chart — the only fetcher that calls SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT for Lifecycle. */
export const fetchTicketingLifecycleResolutionTimePerCategory = async (
	request: TicketingLifecycleRequest,
	lookup?: CategoryLookup,
): Promise<TicketingLifecyclePayload['resolutionTimePerCategory']> => {
	const body = await buildSemanticFilterBody(request);
	const categoryBreakdown = await postSemantic(SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT, body);

	const categoryRows = new Map<string, { totalMinutes: number; weight: number; sub: Map<string, { totalMinutes: number; weight: number }> }>();
	asArray<Record<string, unknown>>(categoryBreakdown?.categories).forEach((row) => {
		const categoryName = resolveName(row.categoryId, lookup?.categoryIdToName);
		if (!categoryName) {
			return;
		}

		const subCategoryName = resolveName(row.subCategoryId, lookup?.subCategoryIdToName);
		const weight = asNumber(row.closedCount) || asNumber(row.totalCount) || 1;
		const avgResolutionMinutes = asNumber(row.avgResolutionMinutes);

		const categoryEntry = categoryRows.get(categoryName) ?? {
			totalMinutes: 0,
			weight: 0,
			sub: new Map(),
		};
		categoryEntry.totalMinutes += avgResolutionMinutes * weight;
		categoryEntry.weight += weight;

		if (subCategoryName) {
			const subEntry = categoryEntry.sub.get(subCategoryName) ?? {
				totalMinutes: 0,
				weight: 0,
			};
			subEntry.totalMinutes += avgResolutionMinutes * weight;
			subEntry.weight += weight;
			categoryEntry.sub.set(subCategoryName, subEntry);
		}

		categoryRows.set(categoryName, categoryEntry);
	});

	return {
		title: 'Resolution Time Per Category',
		selectedCategory: null,
		categories: Array.from(categoryRows.entries()).map(([category, value]) => {
			const avgMinutes = value.weight ? value.totalMinutes / value.weight : 0;
			const categoryDisplay = toDisplayTimeValue(avgMinutes);
			return {
				category,
				value: categoryDisplay.value,
				unit: categoryDisplay.unit,
				subCategories: Array.from(value.sub.entries()).map(([name, subValue]) => {
					const subAvgMinutes = subValue.weight ? subValue.totalMinutes / subValue.weight : 0;
					const subDisplay = toDisplayTimeValue(subAvgMinutes);
					return {
						name,
						value: subDisplay.value,
						unit: subDisplay.unit,
					};
				}),
			};
		}),
	};
};

/**
 * Fetches Resolution Time by Status scoped to a single category (or all
 * categories when `categoryId` is null / 'all'). Used by the "Resolution
 * Time by Status" card on the Lifecycle tab, which lets the user switch the
 * category independently of the page-level category filter and refetches
 * just this card's data (with its own loading state) rather than the whole
 * Lifecycle payload.
 */
export const fetchTicketingStatusAvgTimeByCategory = async (
	request: TicketingLifecycleRequest,
	categoryIds: string[] | null,
): Promise<LifecycleStatusData[]> => {
	const body = await buildSemanticFilterBody(request);
	const normalizedCategories = Array.isArray(categoryIds)
		? categoryIds.filter((c) => !!c && String(c).toLowerCase() !== 'all')
		: [];

	const filteredBody: TicketingAnalyticsSemanticFilter = {
		...body,
		categories: normalizedCategories.length ? normalizedCategories : [],
	};

	const statusAvgTime = await postSemantic(SEMANTIC_STATUS_AVG_TIME_ENDPOINT, filteredBody);
	const statuses = asArray<Record<string, unknown>>(statusAvgTime?.statuses);

	// Deduplicate statuses by status name, keeping the first occurrence
	const uniqueStatusMap = new Map<string, Record<string, unknown>>();
	statuses.forEach((item) => {
		const statusName = asString(item.status).toLowerCase().trim();
		if (statusName && !uniqueStatusMap.has(statusName)) {
			uniqueStatusMap.set(statusName, item);
		}
	});

	// Convert back to array and map to the return type
	return Array.from(uniqueStatusMap.values()).map((item) => {
		const display = toDisplayTimeValue(asNumber(item.avgDurationMinutes));
		return {
			status: asString(item.status),
			value: display.value,
			unit: display.unit,
		};
	});
};

const fetchSemanticCategories = async (
	request: TicketingCategoriesRequest,
	lookup?: CategoryLookup,
): Promise<TicketingCategoriesPayload> => {
	const body = await buildSemanticFilterBody(request);

	// Use semantic endpoints only (reporting endpoints not available)
	const [categoryBreakdown, categoryTrend] = await Promise.all([
		postSemantic(SEMANTIC_CATEGORY_BREAKDOWN_ENDPOINT, body),
		postSemanticWithParams(SEMANTIC_CATEGORY_TREND_ENDPOINT, body, { weeks: 4 }),
	]);

	const useReportingData = false;

	const rows = asArray<Record<string, unknown>>(categoryBreakdown?.categories);
	if (!rows.length) {
		return {
			categoryBreakdown: {
				selectedView: 'total',
				selectedCategory: null,
				total: { categories: [] },
				open: { categories: [] },
			},
			topSubCategories: {
				selectedView: 'total',
				total: [],
				open: [],
			},
			categoryTrend: [],
		};
	}

	type CategoryAccumulator = {
		id: number;
		name: string;
		totalCount: number;
		openCount: number;
		subCategories: Map<string, { id: number; totalCount: number; openCount: number }>;
	};

	const categoryMap = new Map<string, CategoryAccumulator>();
	rows.forEach((row) => {
		// Reporting endpoints return { id, name } directly; semantic endpoints return numeric categoryId
		const categoryName = useReportingData
			? asString(row.name ?? row.categoryName)
			: resolveName(row.categoryId, lookup?.categoryIdToName);

		if (!categoryName) {
			return;
		}

		const categoryId = useReportingData
			? asNumber(row.id ?? row.categoryId, buildCategoryId(categoryName))
			: (typeof row.categoryId === 'number' ? row.categoryId : buildCategoryId(categoryName));

		const categoryEntry = categoryMap.get(categoryName) ?? {
			id: categoryId,
			name: categoryName,
			totalCount: 0,
			openCount: 0,
			subCategories: new Map(),
		};

		categoryEntry.totalCount += asNumber(row.totalCount);
		categoryEntry.openCount += asNumber(row.openCount);

		// Reporting endpoints: subCategories may be nested in the row or at row.subCategories
		if (useReportingData && Array.isArray(row.subCategories)) {
			// Reporting endpoint returns nested subCategories array
			(row.subCategories as Record<string, unknown>[]).forEach((sub) => {
				const subName = asString(sub.name ?? sub.subCategoryName);
				const subId = asNumber(sub.id ?? sub.subCategoryId, buildCategoryId(`${categoryName}:${subName}`));
				if (!subName) return;
				const subEntry = categoryEntry.subCategories.get(subName) ?? { id: subId, totalCount: 0, openCount: 0 };
				subEntry.totalCount += asNumber(sub.totalCount);
				subEntry.openCount += asNumber(sub.openCount);
				categoryEntry.subCategories.set(subName, subEntry);
			});
		} else if (!useReportingData) {
			// Semantic endpoint: resolve numeric subCategoryId → human-readable name
			const subCategoryName = resolveName(row.subCategoryId, lookup?.subCategoryIdToName);
			if (subCategoryName) {
				const subId = buildCategoryId(`${categoryName}:${subCategoryName}`);
				const subEntry = categoryEntry.subCategories.get(subCategoryName) ?? { id: subId, totalCount: 0, openCount: 0 };
				subEntry.totalCount += asNumber(row.totalCount);
				subEntry.openCount += asNumber(row.openCount);
				categoryEntry.subCategories.set(subCategoryName, subEntry);
			}
		}

		categoryMap.set(categoryName, categoryEntry);
	});

	const categoryList = Array.from(categoryMap.values());
	const totalCategoryCount = categoryList.reduce((sum, category) => sum + category.totalCount, 0);
	const openCategoryCount = categoryList.reduce((sum, category) => sum + category.openCount, 0);

	const toCategoryView = (mode: 'total' | 'open') => ({
		categories: categoryList
			.map((category) => {
				const count = mode === 'total' ? category.totalCount : category.openCount;
				const denominator = mode === 'total' ? totalCategoryCount : openCategoryCount;
				const subTotal = Array.from(category.subCategories.values()).reduce(
					(sum, subCategory) => sum + (mode === 'total' ? subCategory.totalCount : subCategory.openCount),
					0,
				);

				return {
					id: category.id,
					name: category.name,
					count,
					percentage: safePercent(count, denominator),
					subCategories: Array.from(category.subCategories.entries())
						.map(([name, subCategory]) => {
							const subCount = mode === 'total' ? subCategory.totalCount : subCategory.openCount;
							return {
								id: buildCategoryId(`${category.name}:${name}`),
								name,
								count: subCount,
								percentage: safePercent(subCount, subTotal),
							};
						})
						.sort((left, right) => right.count - left.count),
				};
			})
			.sort((left, right) => right.count - left.count),
	});

	// Build sub-category rows, deduplicating by (category + name) key
	const totalSubMap = new Map<string, { name: string; category: string; count: number }>();
	const openSubMap = new Map<string, { name: string; category: string; count: number }>();

	categoryList.forEach((category) => {
		Array.from(category.subCategories.entries()).forEach(([name, value]) => {
			const key = `${category.name}|||${name}`;
			const existing = totalSubMap.get(key);
			totalSubMap.set(key, {
				name,
				category: category.name,
				count: (existing?.count ?? 0) + value.totalCount,
			});
			const existingOpen = openSubMap.get(key);
			openSubMap.set(key, {
				name,
				category: category.name,
				count: (existingOpen?.count ?? 0) + value.openCount,
			});
		});
	});

	const totalSubRows = Array.from(totalSubMap.values());
	const openSubRows = Array.from(openSubMap.values());
	const totalSubCount = totalSubRows.reduce((sum, row) => sum + row.count, 0);
	const openSubCount = openSubRows.reduce((sum, row) => sum + row.count, 0);

	const trendFromCategoryTrends = asArray<Record<string, unknown>>(categoryTrend?.categoryTrends);
	const trendRowsRaw = asArray<Record<string, unknown>>(categoryTrend?.rows);
	
	// Extract week labels from API response (e.g., "2026-W27" → "W27", keep "LCW" as is)
	const rawWeekLabels = asArray<string>(categoryTrend?.weekLabels ?? []);
	const weekLabels = rawWeekLabels.map(label => {
		// If label contains "LCW", keep it as is
		if (label.includes('LCW')) {
			return 'LCW';
		}
		// Extract week number from format like "2026-W27" → "W27"
		const match = label.match(/W\d+/);
		return match ? match[0] : label;
	});
	
	console.log('[CategoryTrend API] Raw API response keys:', Object.keys(categoryTrend ?? {}));
	console.log('[CategoryTrend API] categoryTrends length:', trendFromCategoryTrends.length);
	console.log('[CategoryTrend API] trendRowsRaw length:', trendRowsRaw.length);
	console.log('[CategoryTrend API] Raw week labels from API:', rawWeekLabels);
	console.log('[CategoryTrend API] Parsed week labels:', weekLabels);
	
	if (trendFromCategoryTrends.length > 0) {
		console.log('[CategoryTrend API] First category sample:', JSON.stringify(trendFromCategoryTrends[0], null, 2));
	}
	
	if (trendRowsRaw.length > 0) {
		console.log('[CategoryTrend API] First raw row sample:', JSON.stringify(trendRowsRaw[0], null, 2));
	}

	const categoryTrendRows = trendFromCategoryTrends.length
		? trendFromCategoryTrends.map((trend) => {
			// Reporting endpoint returns { name, ... }; semantic endpoint returns { categoryId, ... }
			const categoryName = useReportingData
				? asString(trend.name ?? trend.categoryName ?? trend.category)
				: resolveName(trend.categoryId ?? trend.id, lookup?.categoryIdToName);

			const volumeValues = asArray<number>(trend.weeklyVolumes);
			const percentageChange = asNumber(trend.percentageChange);
			
			// Map weekly volumes with actual week labels from API
			const weeks = volumeValues.map((value, index) => ({
				label: weekLabels[index] || `W${index + 1}`,
				value: asNumber(value),
			}));
			
			// Get subcategories from WITHIN this trend object (not root level)
			const rawSubCategories = asArray<Record<string, unknown>>(trend.subCategories ?? []);
			
			console.log(`[CategoryTrend API] Category "${categoryName}" has ${rawSubCategories.length} subcategories in trend object`);
			
			const subCategories = rawSubCategories
				.map((sub) => {
					// Reporting endpoint: sub.name is already human-readable
					// Semantic endpoint: resolve via sub.subCategoryId
					const subName = useReportingData
						? asString(sub.name ?? sub.subCategoryName)
						: resolveName(
							sub.subCategoryId ?? sub.id ?? sub.name,
							lookup?.subCategoryIdToName,
							asString(sub.subCategoryId ?? sub.id ?? sub.name)
						);
					
					// Get week volumes with actual week labels
					const subVolumeValues = asArray<number>(sub.weeklyVolumes ?? sub.volumes ?? []);
					const subWeeks = subVolumeValues.map((value, index) => ({
						label: weekLabels[index] || `W${index + 1}`,
						value: asNumber(value),
					}));
					
					// Only return if we have a name and at least one week value
					if (subName && subWeeks.length > 0) {
						return {
							name: subName,
							weeks: subWeeks,
							percentageChange: asNumber(sub.percentageChange),
						};
					}
					return null;
				})
				.filter((item): item is { name: string; weeks: Array<{ label: string; value: number }>; percentageChange: number } => item !== null);

			if (subCategories.length > 0) {
				console.log(`[CategoryTrend API] Category "${categoryName}" has ${subCategories.length} subcategories:`, subCategories.map(s => s?.name));
			}

			return {
				category: categoryName,
				weeks,
				percentageChange: percentageChange !== 0 ? percentageChange : undefined,
				subCategories: subCategories.length > 0 ? subCategories : undefined,
			};
		})
		: Array.from(
			trendRowsRaw.reduce((map, row) => {
				const category = resolveName(row.categoryId, lookup?.categoryIdToName);
				if (!category) {
					return map;
				}
				
				// Use weekLabels if available, otherwise parse from row.weekLabel
				let weekLabel = asString(row.weekLabel);
				if (weekLabels.length > 0 && row.weekIndex !== undefined) {
					const idx = asNumber(row.weekIndex);
					weekLabel = weekLabels[idx] || weekLabel;
				}
				
				const current = map.get(category) ?? [];
				current.push({
					label: weekLabel,
					value: asNumber(row.ticketCount),
				});
				map.set(category, current);
				return map;
			}, new Map<string, Array<{ label: string; value: number }>>()),
		).map(([category, weeks]) => {
			console.log(`[CategoryTrend API FALLBACK] Category "${category}" has ${weeks.length} weeks:`, weeks.map(w => w.label).join(', '));
			return {
				category,
				weeks,
			};
		});

	return {
		categoryBreakdown: {
			selectedView: 'total',
			selectedCategory: null,
			total: toCategoryView('total'),
			open: toCategoryView('open'),
		},
		topSubCategories: {
			selectedView: 'total',
			total: totalSubRows
				.sort((left, right) => right.count - left.count)
				.slice(0, 10)
				.map((row) => ({
					name: row.name,
					category: row.category,
					count: row.count,
					percentage: safePercent(row.count, totalSubCount),
				})),
			open: openSubRows
				.sort((left, right) => right.count - left.count)
				.slice(0, 10)
				.map((row) => ({
					name: row.name,
					category: row.category,
					count: row.count,
					percentage: safePercent(row.count, openSubCount),
				})),
		},
		categoryTrend: categoryTrendRows,
	};
};

const fetchSemanticTeamWorkload = async (
	request: TicketingTeamWorkloadRequest,
): Promise<TicketingTeamWorkloadPayload> => {
	const body = await buildSemanticFilterBody(request);
	const [agentWorkload, overviewSummary, agentStatusAvgTime] = await Promise.all([
		postSemantic(SEMANTIC_AGENT_WORKLOAD_ENDPOINT, body),
		postSemantic(SEMANTIC_OVERVIEW_ENDPOINT, body),
		postSemantic(SEMANTIC_AGENT_STATUS_AVG_TIME_ENDPOINT, body),
	]);

	const agents = asArray<Record<string, unknown>>(agentWorkload?.agents);
	if (!agents.length) {
		// Backend returned 0 agents for this filter — return empty payload instead of throwing.
		return {
			overview: [
				{ key: 'active_agents', title: 'Active Agents', value: 0, unit: 'agents', trend: null, description: 'Within configured working hours now' },
				{ key: 'average_tickets_per_agent', title: 'Average Tickets per Agent', value: 0, unit: 'tickets', trend: null, description: '0 assigned across 0 agents' },
				{ key: 'busiest_agent', title: 'Busiest Agent', value: 'N/A', unit: null, trend: null, description: '0 assigned' },
				{ key: 'unassigned_tickets', title: 'Unassigned Tickets', value: 0, unit: 'tickets', trend: null, description: 'Open tickets currently without assignee' },
			],
			ticketDistribution: { title: 'Ticket Distribution', series: [] },
			ticketsByTeamMember: [],
		};
	}

	const totalAssigned = agents.reduce((sum, agent) => sum + asNumber(agent.totalTickets), 0);
	const activeAgents = agents.filter((agent) => asNumber(agent.totalTickets) > 0).length;
	const averageTicketsPerAgent = activeAgents ? totalAssigned / activeAgents : 0;
	const busiestAgent = agents.reduce<Record<string, unknown> | null>((current, agent) => {
		if (!current) {
			return agent;
		}
		return asNumber(agent.totalTickets) > asNumber(current.totalTickets) ? agent : current;
	}, null);

	const busiestCount = asNumber(busiestAgent?.totalTickets);
	const busiestName = asString(busiestAgent?.assignee, 'N/A');

	// ── Fetch previous period busiest agent for comparison ─────────────────────
	let busiestAgentTrend: { value: number; unit: 'percent'; direction: 'up' | 'down'; impact: 'positive' | 'negative' | null } | null = null;
	const tpf = request.timePeriodFilter;
	if (tpf && tpf.filter !== 'custom' && busiestCount > 0) {
		try {
			const prevDates = await fetchPreviousPeriodDates(tpf);
			if (prevDates) {
				const prevBody: TicketingAnalyticsSemanticFilter = {
					...body,
					fromDate: prevDates.fromDate,
					toDate: prevDates.toDate,
				};
				const prevAgentWorkload = await postSemantic(SEMANTIC_AGENT_WORKLOAD_ENDPOINT, prevBody);
				const prevAgents = asArray<Record<string, unknown>>(prevAgentWorkload?.agents);
				const prevBusiest = prevAgents.reduce<Record<string, unknown> | null>((current, agent) => {
					if (!current) return agent;
					return asNumber(agent.totalTickets) > asNumber(current.totalTickets) ? agent : current;
				}, null);
				const prevBusiestCount = asNumber(prevBusiest?.totalTickets);
				if (prevBusiestCount > 0) {
					const pctChange = calculatePercentChange(busiestCount, prevBusiestCount);
					if (pctChange !== null) {
						const direction = pctChange >= 0 ? 'up' as const : 'down' as const;
						busiestAgentTrend = metricTrend(Math.abs(pctChange), direction, getMetricTrendImpact('busiest_agent', direction));
					}
				}
			}
		} catch (err) {
			console.warn('[TicketingAnalytics] Previous period busiest agent comparison failed:', err);
		}
	}

	const toTimeUnit = (minutes: number) => {
		const display = toDisplayTimeValue(minutes);
		return { value: display.value, unit: display.unit };
	};

	// Build a map of assignee email to status-based avg times
	const agentStatusTimeMap = new Map<string, Record<string, number>>();
	asArray<Record<string, unknown>>(agentStatusAvgTime?.agents).forEach((agent) => {
		const assignee = asString(agent.assignee);
		if (!assignee) return;
		const avgMinutesByStatus = asObject(agent.avgMinutesByStatus);
		if (avgMinutesByStatus) {
			const timesByStatus: Record<string, number> = {};
			Object.entries(avgMinutesByStatus).forEach(([status, minutes]) => {
				const normalizedStatus = normalizeStatusName(status);
				timesByStatus[normalizedStatus] = asNumber(minutes);
			});
			agentStatusTimeMap.set(assignee, timesByStatus);
		}
	});

	// Format busiest agent description with trend if available
	let busiestAgentDesc = `${busiestCount.toLocaleString()} assigned`;
	if (busiestAgentTrend) {
		busiestAgentDesc = `${busiestCount.toLocaleString()} assigned · ${busiestAgentTrend.value.toFixed(0)}% above average`;
	}

	// Format unassigned tickets time description
	const avgUnassignedTimeMinutes = asNumber(overviewSummary?.avgUnassignedTimeMinutes);
	const unassignedTimeDisplay = toDisplayTimeValue(avgUnassignedTimeMinutes);
	const unassignedDesc = avgUnassignedTimeMinutes > 0 
		? `Avg ${unassignedTimeDisplay.value.toFixed(1)} ${unassignedTimeDisplay.unit} in queue`
		: 'Open tickets currently without assignee';

	return {
		overview: [
			{
				key: 'active_agents',
				title: 'Active Agents',
				value: activeAgents,
				unit: 'agents',
				trend: null,
				description: 'Within configured working hours now',
			},
			{
				key: 'average_tickets_per_agent',
				title: 'Average Tickets per Agent',
				value: roundToOneDecimal(averageTicketsPerAgent),
				unit: 'tickets',
				trend: null,
				description: `${totalAssigned.toLocaleString()} assigned across ${activeAgents} agents`,
			},
			{
				key: 'busiest_agent',
				title: 'Busiest Agent',
				value: busiestName,
				unit: null,
				trend: null,
				description: busiestAgentDesc,
			},
			{
				key: 'unassigned_tickets',
				title: 'Unassigned Tickets',
				value: asNumber(overviewSummary?.unassignedOpenCount),
				unit: 'tickets',
				trend: null,
				description: unassignedDesc,
			},
		],
		ticketDistribution: {
			title: 'Ticket Distribution',
			series: agents.map((agent) => ({
				teamMember: asString(agent.assignee, 'Unassigned'),
				assignedTickets: asNumber(agent.totalTickets),
				openTickets: asNumber(agent.openTickets),
				closedTickets: asNumber(agent.closedTickets),
			})),
		},
		ticketsByTeamMember: agents.map((agent) => {
			const avgResolutionTimeMinutes = asNumber(agent.avgResolutionTimeMinutes);
			const avgResolution = toTimeUnitAsPercentageOfDay(avgResolutionTimeMinutes);
			const assignee = asString(agent.assignee, 'Unassigned');
			const statusTimeMap = agentStatusTimeMap.get(assignee);

			return {
				teamMember: assignee,
				assignedTickets: asNumber(agent.totalTickets),
				openTickets: asNumber(agent.openTickets),
				closedTickets: asNumber(agent.closedTickets),
				averageResolutionTime: avgResolution,
				averageTimeInProgress: toTimeUnitAsPercentageOfDay(asNumber(statusTimeMap?.['In Progress'], 0)),
				averageTimeEscalatedToIT: toTimeUnitAsPercentageOfDay(asNumber(statusTimeMap?.['Escalated to IT'], 0)),
				averageTimeEscalatedToTMV: toTimeUnitAsPercentageOfDay(asNumber(statusTimeMap?.['Escalated to TMV'], 0)),
				averageTimeSubmittedToADO: toTimeUnitAsPercentageOfDay(asNumber(statusTimeMap?.['Submitted to ADO'], 0)),
				averageTimeWaitingOnUser: toTimeUnitAsPercentageOfDay(asNumber(statusTimeMap?.['Waiting on User'], 0)),
				resolutionRate: safePercent(asNumber(agent.closedTickets), asNumber(agent.totalTickets)),
			};
		}),
	};
};

const fetchSemanticSubmittersLocations = async (
	request: TicketingSubmittersLocationsRequest,
	lookup?: CategoryLookup,
): Promise<TicketingSubmittersLocationsPayload> => {
	const body = await buildSemanticFilterBody(request);
	const [roleBreakdown, topSubmitters, topLocations] = await Promise.all([
		postSemantic(SEMANTIC_ROLE_BREAKDOWN_ENDPOINT, body),
		postSemanticWithParams(SEMANTIC_TOP_SUBMITTERS_ENDPOINT, body, { limit: 10 }),
		postSemanticWithParams(SEMANTIC_TOP_LOCATIONS_ENDPOINT, body, { limit: 50 }),
	]);

	const roles = asArray<Record<string, unknown>>(roleBreakdown?.roles);
	const submitters = asArray<Record<string, unknown>>(topSubmitters?.submitters);
	const locations = asArray<Record<string, unknown>>(topLocations?.locations);
	if (!roles.length && !submitters.length && !locations.length) {
		// Backend returned 0 results for this filter — return empty payload.
		return {
			ticketsByRole: { title: 'Tickets by Role', description: 'Total tickets submitted per Navigator role', roles: [] },
			topRepeatSubmitters: { title: 'Top Repeat Submitters', description: 'Top users by total tickets submitted', submitters: [] },
			topLocationSubmitters: { title: 'Top Location Submitters', description: 'Top locations by total tickets submitted', locations: [] },
			masterLocationTable: { title: 'Master Location Table', searchable: true, searchPlaceholder: 'Search locations or SAP ID', locations: [] },
		};
	}

	const totalRoleCount = roles.reduce((sum, role) => sum + asNumber(role.totalCount), 0);

	return {
		ticketsByRole: {
			title: 'Tickets by Role',
			description: 'Total tickets submitted per Navigator role',
			roles: roles.map((role) => ({
				name: asString(role.submitterRole, 'Other'),
				count: asNumber(role.totalCount),
				percentage: safePercent(asNumber(role.totalCount), totalRoleCount),
			})),
		},
		topRepeatSubmitters: {
			title: 'Top Repeat Submitters',
			description: 'Top users by total tickets submitted',
			submitters: submitters.map((submitter) => {
				const total = asNumber(submitter.totalTickets);
				const closed = asNumber(submitter.closedTickets);
				return {
					user: asString(submitter.submitterEmail, asString(submitter.user, 'Unknown')),
					ticketsSubmitted: total,
					ticketsClosed: closed,
					ticketsOpen: asNumber(submitter.openTickets),
					resolutionRate: safePercent(closed, total),
					averageResolutionTime: toHours(asNumber(submitter.avgResolutionMinutes)),
					topCategoryName: resolveName(submitter.topCategoryId ?? submitter.topCategory ?? submitter.topCategoryName, lookup?.categoryIdToName, asString(submitter.topCategoryName, 'N/A')),
					topCategoryCount: asNumber(submitter.topCategoryCount),
					navigatorRole: asString(submitter.topRole ?? submitter.submitterRole, 'N/A'),
					location: asString(submitter.topLocation ?? submitter.location, 'N/A'),
				};
			}),
		},
		topLocationSubmitters: {
			title: 'Top Location Submitters',
			description: 'Top locations by total tickets submitted',
			locations: locations
				.filter((location) => asString(location.ticketLevel).toLowerCase() === 'location')
				.slice(0, 10)
				.map((location) => {
					const total = asNumber(location.totalTickets);
					const closed = asNumber(location.closedTickets);
					const locationName = asString(location.levelReferenceId, asString(location.location, 'Unknown'));
					
					return {
						location: locationName,
						ticketsSubmitted: total,
						ticketsClosed: closed,
						ticketsOpen: asNumber(location.openTickets),
						resolutionRate: safePercent(closed, total),
						averageResolutionTime: toHours(asNumber(location.avgResolutionMinutes)),
						topCategoryName: resolveName(location.topCategoryId ?? location.topCategory ?? location.topCategoryName, lookup?.categoryIdToName, asString(location.topCategoryName, 'N/A')),
						topCategoryCount: asNumber(location.topCategoryCount),
					};
				}),
		},
		masterLocationTable: {
			title: 'Master Location Table',
			searchable: true,
			searchPlaceholder: 'Search locations or SAP ID',
			locations: locations
				.filter((location) => asString(location.ticketLevel).toLowerCase() === 'location')
				.map((location) => {
					const total = asNumber(location.totalTickets);
					const closed = asNumber(location.closedTickets);
					const locationName = asString(location.levelReferenceId, asString(location.location, 'Unknown'));
					
					return {
						location: locationName,
						sapId: asNumber(location.locationSapId),
						ticketsSubmitted: total,
						ticketsClosed: closed,
						ticketsOpen: asNumber(location.openTickets),
						resolutionRate: safePercent(closed, total),
						averageResolutionTime: toHours(asNumber(location.avgResolutionMinutes)),
						topCategoryName: resolveName(location.topCategoryId ?? location.topCategory ?? location.topCategoryName, lookup?.categoryIdToName, asString(location.topCategoryName, 'N/A')),
						topCategoryCount: asNumber(location.topCategoryCount),
					};
				}),
		},
	};
};

const fetchSemanticRca = async (request: TicketingRcaRequest, lookup?: CategoryLookup): Promise<TicketingRcaPayload> => {
	const body = await buildSemanticFilterBody(request);
	const rcaBreakdown = await postSemantic(SEMANTIC_RCA_BREAKDOWN_ENDPOINT, body);
	const rows = asArray<Record<string, unknown>>(rcaBreakdown?.rows);
	if (!rows.length) {
		return {
			resolutionByRcaCategory: {
				title: 'Resolution by RCA Category',
				description: 'Total tickets attributed to each root cause category.',
				categories: [],
			},
			rcaByCategory: {
				title: 'RCA by Category',
				description: 'Ticket concentration by RCA category and ticket category.',
				categories: [],
				rcaCategories: [],
				matrix: [],
				subCategoryMatrix: [],
				subCategoriesByCategory: {},
			},
		};
	}

	const totalTickets = rows.reduce((sum, row) => sum + asNumber(row.totalCount), 0);
	const countsByRca = new Map<string, number>();
	const categoriesSet = new Set<string>();
	const rcaSet = new Set<string>();
	const subCategoriesByCategory = new Map<string, Set<string>>();
	const rcaBySubCategory = new Map<string, Map<string, number>>();

	rows.forEach((row) => {
		const rcaCategory = asString(row.rcaCategory);
		// Resolve numeric categoryId → human-readable name
		const category = resolveName(row.categoryId, lookup?.categoryIdToName);
		const subCategoryId = row.subCategoryId ?? row.subCategoryName;
		const subCategory = subCategoryId 
			? resolveName(subCategoryId, lookup?.subCategoryIdToName) 
			: undefined;
		
		if (!rcaCategory || !category) {
			return;
		}

		rcaSet.add(rcaCategory);
		categoriesSet.add(category);
		countsByRca.set(rcaCategory, (countsByRca.get(rcaCategory) ?? 0) + asNumber(row.totalCount));

		// Track sub-categories for each category
		if (subCategory) {
			if (!subCategoriesByCategory.has(category)) {
				subCategoriesByCategory.set(category, new Set());
			}
			subCategoriesByCategory.get(category)?.add(subCategory);

			// Build RCA matrix for sub-categories
			const key = `${category}|${subCategory}`;
			if (!rcaBySubCategory.has(rcaCategory)) {
				rcaBySubCategory.set(rcaCategory, new Map());
			}
			const subMap = rcaBySubCategory.get(rcaCategory)!;
			subMap.set(key, (subMap.get(key) ?? 0) + asNumber(row.totalCount));
		}
	});

	const categories = Array.from(categoriesSet.values());
	const matrixByRca = new Map<string, Record<string, number>>();
	const subCategoryMatrixByRca = new Map<string, Record<string, Record<string, number>>>();

	rows.forEach((row) => {
		const rcaCategory = asString(row.rcaCategory);
		// Resolve numeric categoryId → human-readable name
		const category = resolveName(row.categoryId, lookup?.categoryIdToName);
		if (!rcaCategory || !category) {
			return;
		}

		const entry = matrixByRca.get(rcaCategory) ?? {};
		entry[category] = (entry[category] ?? 0) + asNumber(row.totalCount);
		matrixByRca.set(rcaCategory, entry);

		// Build sub-category matrix
		const subCategoryId = row.subCategoryId ?? row.subCategoryName;
		const subCategory = subCategoryId 
			? resolveName(subCategoryId, lookup?.subCategoryIdToName) 
			: undefined;
		
		if (subCategory) {
			const subEntry = subCategoryMatrixByRca.get(rcaCategory) ?? {};
			const categoryEntry = subEntry[category] ?? {};
			categoryEntry[subCategory] = (categoryEntry[subCategory] ?? 0) + asNumber(row.totalCount);
			subEntry[category] = categoryEntry;
			subCategoryMatrixByRca.set(rcaCategory, subEntry);
		}
	});

	const resolutionByRcaCategory = Array.from(countsByRca.entries())
		.map(([name, count]) => ({
			name,
			count,
			percentage: safePercent(count, totalTickets),
		}))
		.sort((left, right) => right.count - left.count);

	console.log('[RCA API] subCategoryMatrixByRca:', subCategoryMatrixByRca);
	console.log('[RCA API] subCategoriesByCategory:', subCategoriesByCategory);

	return {
		resolutionByRcaCategory: {
			title: 'Resolution by RCA Category',
			description: 'Total tickets attributed to each root cause category.',
			categories: resolutionByRcaCategory,
		},
		rcaByCategory: {
			title: 'RCA by Category',
			description: 'Ticket concentration by RCA category and ticket category.',
			categories,
			rcaCategories: Array.from(rcaSet.values()),
			matrix: Array.from(matrixByRca.entries()).map(([rcaCategory, values]) => ({
				rcaCategory,
				values,
			})),
			subCategoryMatrix: Array.from(subCategoryMatrixByRca.entries()).map(([rcaCategory, categoryMap]) => ({
				rcaCategory,
				categorySubCategoryValues: categoryMap,
			})),
			subCategoriesByCategory: Object.fromEntries(
				Array.from(subCategoriesByCategory.entries()).map(([category, subs]) => [
					category,
					Array.from(subs),
				])
			),
		},
	};
};

export const fetchNavigatorTicketingOverview = async (
	request: TicketingOverviewRequest,
	lookup?: CategoryLookup,
): Promise<TicketingOverviewPayload> => {
	return fetchSemanticOverview(request, lookup);
};

export const fetchNavigatorTicketingVolumeTrends = async (
	request: TicketingVolumeTrendsRequest,
): Promise<TicketingVolumeTrendsPayload> => {
	return fetchSemanticVolumeTrends(request);
};

export const fetchNavigatorTicketingTeamWorkload = async (
	request: TicketingTeamWorkloadRequest,
): Promise<TicketingTeamWorkloadPayload> => {
	return fetchSemanticTeamWorkload(request);
};

export const fetchNavigatorTicketingRca = async (
	request: TicketingRcaRequest,
	lookup?: CategoryLookup,
): Promise<TicketingRcaPayload> => {
	return fetchSemanticRca(request, lookup);
};

export const fetchNavigatorTicketingLifecycle = async (
	request: TicketingLifecycleRequest,
	lookup?: CategoryLookup,
): Promise<TicketingLifecyclePayload> => {
	return fetchSemanticLifecycle(request, lookup);
};

export const fetchNavigatorTicketingCategories = async (
	request: TicketingCategoriesRequest,
	lookup?: CategoryLookup,
): Promise<TicketingCategoriesPayload> => {
	return fetchSemanticCategories(request, lookup);
};

export const fetchNavigatorTicketingSubmittersLocations = async (
	request: TicketingSubmittersLocationsRequest,
	lookup?: CategoryLookup,
): Promise<TicketingSubmittersLocationsPayload> => {
	return fetchSemanticSubmittersLocations(request, lookup);
};

const TICKET_ASSIGNEES_ENDPOINT = '/ticketing/tickets/assignees/unique';

/**
 * Fetch the distinct list of ticket assignees from the dedicated
 * GET /ticketing/tickets/assignees/unique endpoint.
 *
 * This endpoint returns a flat array of unique assignedTo values — no
 * pagination needed. It avoids scanning all ticket records in the frontend.
 *
 * Returns one KPIFilterDropdownItem per unique assignee (email converted to
 * display name via formatUserName), sorted A→Z.
 * Falls back to an empty array on error so the filter panel degrades gracefully.
 */
export const fetchTicketingAssignedMembers = async (): Promise<Array<{ id: string; displayText: string }>> => {
	const ticketApi = createApiClient();
	try {
		const response = await ticketApi.get<{
			success?: boolean;
			message?: string;
			totalCount?: number;
			data?: {
				assignees?: string[];
			} | string[];
		}>(TICKET_ASSIGNEES_ENDPOINT);

		// API response shape: { success, message, totalCount, data: { assignees: string[] } }
		// Also handles legacy flat shapes: { data: string[] } or plain string[]
		const raw = response?.data;
		let values: string[] = [];

		if (Array.isArray(raw)) {
			// Plain string[] at top level
			values = raw as unknown as string[];
		} else if (raw && Array.isArray((raw as any).assignees)) {
			// { assignees: string[] } directly on raw (shouldn't normally happen but be safe)
			values = (raw as any).assignees;
		} else if (raw && typeof raw === 'object' && 'data' in raw) {
			const nested = (raw as any).data;
			if (Array.isArray(nested)) {
				// { data: string[] }
				values = nested;
			} else if (nested && Array.isArray(nested.assignees)) {
				// { data: { assignees: string[] } }  ← actual API shape
				values = nested.assignees;
			}
		}

		// Deduplicate by normalized display name.
		// The API may return both a plain display name ("Amit Belavadi") and the
		// matching email ("Amit.Belavadi.Contractor@pepsico.com") for the same person.
		//
		// Strategy:
		//   • id        = always the EMAIL (backend recognises assignees by email only)
		//   • displayText = human-readable name shown in the dropdown
		//   • Dedup key = lowercased display name derived from the email/name
		//   • When both a name and an email exist for the same person, the email wins as id.
		//   • Plain names with no matching email are kept as-is (id = plain name) as a fallback.

		// Pass 1 — collect everything keyed by normalised display name
		// Map: normalisedDisplayName → { email: string | null, plainName: string | null }
		const byKey = new Map<string, { email: string | null; plainName: string | null }>();

		values.forEach((value) => {
			const v = typeof value === 'string' ? value.trim() : '';
			if (!v || v.toLowerCase() === 'unassigned') return;

			const isEmail = v.includes('@');
			const displayText = isEmail ? formatUserName(v) : v;
			const key = displayText.toLowerCase();

			const existing = byKey.get(key) ?? { email: null, plainName: null };

			if (isEmail) {
				// Always prefer/overwrite with email so we always capture the latest one
				existing.email = v;
			} else {
				// Only set plain name if we don't already have one (first wins for display)
				if (!existing.plainName) {
					existing.plainName = v;
				}
			}

			byKey.set(key, existing);
		});

		// Pass 2 — build final items:
		//   id          = email (preferred) or plain name fallback
		//   displayText = plain name (preferred) or formatUserName(email) fallback
		return Array.from(byKey.entries())
			.map(([, entry]) => {
				const id = entry.email ?? entry.plainName ?? '';
				const displayText = entry.plainName ?? (entry.email ? formatUserName(entry.email) : '');
				return { id, displayText };
			})
			.filter((item) => item.id.length > 0)
			.sort((a, b) => a.displayText.localeCompare(b.displayText));
	} catch (err) {
		console.warn('[fetchTicketingAssignedMembers] Failed to load assignees:', err);
		return [];
	}
};

const TICKETS_EXPORT_ENDPOINT = '/analytics/ticketing/tickets/export';
const CATEGORY_TREND_EXPORT_ENDPOINT = '/analytics/ticketing/category-trend/export';

/**
 * Export all tickets matching the current applied filters as an Excel file.
 * Calls POST /analytics/ticketing/tickets/export with the same filter body
 * used by all other analytics endpoints, then triggers a browser download.
 *
 * @param request The current filter/date-range request (same shape as other analytics calls)
 */
export const exportTicketsData = async (
	request: TicketingRequestWithFilters,
): Promise<void> => {
	try {
		const exportApi = createApiClient();
		const body = await buildSemanticFilterBody(request);

		console.log('[exportTicketsData] POST', TICKETS_EXPORT_ENDPOINT, body);

		const response = await exportApi.post(TICKETS_EXPORT_ENDPOINT, body, {
			responseType: 'blob',
		});

		const blob = response.data as Blob;
		if (!blob || blob.size === 0) {
			throw new Error('Received empty response from tickets export API');
		}

		// Determine filename from Content-Disposition header or fallback
		const contentDisposition: string = response.headers['content-disposition'] ?? '';
		let filename = 'ticketing-analytics-export.xlsx';
		if (contentDisposition.includes('filename')) {
			const match = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"';]*)\1/);
			if (match?.[2]) {
				filename = match[2];
			}
		}

		console.log('[exportTicketsData] Downloading as:', filename);

		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			setTimeout(() => {
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				console.log('[exportTicketsData] Download cleanup completed');
			}, 100);
			console.log('[exportTicketsData] Export completed successfully');
		} else {
			throw new Error('Download feature only available in web browser');
		}
	} catch (error) {
		console.error('[exportTicketsData] Export failed:', error);
		const errorMessage = error instanceof Error ? error.message : 'Unknown error during export';
		throw new Error(`Failed to export tickets data: ${errorMessage}`);
	}
};

/**
 * Export category trend data for 52 weeks as an Excel file
 * @param weeks Number of weeks to export (default: 52)
 * @param categoryMapping Optional mapping of category IDs to names { categoryId: categoryName }
 * @returns Promise that resolves when file is downloaded
 */
export const exportCategoryTrendData = async (
	weeks: number = 52,
	categoryMapping?: Record<string, string>
): Promise<void> => {
	try {
		const exportApi = createApiClient();

		// Build request body with optional category mapping
		const requestBody: any = {};
		if (categoryMapping && Object.keys(categoryMapping).length > 0) {
			requestBody.categoryMapping = categoryMapping;
			console.log('[exportCategoryTrendData] Sending category mapping:', categoryMapping);
			console.log('[exportCategoryTrendData] Mapping size:', Object.keys(categoryMapping).length);
			console.log('[exportCategoryTrendData] Request body:', JSON.stringify(requestBody, null, 2));
		} else {
			console.warn('[exportCategoryTrendData] No category mapping provided');
		}

		console.log('[exportCategoryTrendData] Making POST request to:', `${CATEGORY_TREND_EXPORT_ENDPOINT}?weeks=${weeks}`);
		console.log('[exportCategoryTrendData] Request body keys:', Object.keys(requestBody));

		// Make POST request to export endpoint with blob response type
		const response = await exportApi.post(
			`${CATEGORY_TREND_EXPORT_ENDPOINT}?weeks=${weeks}`,
			requestBody, // Pass category mapping in body if provided
			{
				responseType: 'blob' // Request binary response (blob)
			}
		);

		console.log('[exportCategoryTrendData] Response received, status:', response.status);
		console.log('[exportCategoryTrendData] Response headers:', response.headers);

		// Get the blob from response data
		const blob = response.data as Blob;

		if (!blob || blob.size === 0) {
			throw new Error('Received empty response from export API');
		}

		// Get filename from Content-Disposition header or use default
		const contentDisposition = response.headers['content-disposition'] || '';
		let filename = `category-trend-${weeks}-weeks.xlsx`;

		if (contentDisposition.includes('filename')) {
			const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"';]*)\1/);
			if (filenameMatch && filenameMatch[2]) {
				filename = filenameMatch[2];
			}
		}

		console.log('[exportCategoryTrendData] Export filename:', filename);

		// For web environment: Use browser download
		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			// Create a temporary object URL for the blob
			const url = URL.createObjectURL(blob);

			// Create a temporary anchor element to trigger download
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			link.style.display = 'none';

			// Append to body, click, and cleanup
			document.body.appendChild(link);
			link.click();

			// Cleanup after a short delay to ensure download starts
			setTimeout(() => {
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				console.log('[exportCategoryTrendData] Download cleanup completed');
			}, 100);

			console.log('[exportCategoryTrendData] Excel export completed successfully');
		} else {
			// For native environments or when document/window is unavailable
			console.warn('[exportCategoryTrendData] Browser environment not available for download');
			throw new Error('Download feature only available in web browser');
		}
	} catch (error) {
		console.error('[exportCategoryTrendData] Export failed:', error);
		const errorMessage = error instanceof Error ? error.message : 'Unknown error during export';
		throw new Error(`Failed to export category trend data: ${errorMessage}`);
	}
};

// Phase 1 keeps runtime behavior delegated to the legacy Ticketing screen, but
// this file is now the NavigatorReports-local data boundary for future tab migration.
export const navigatorTicketingAnalyticsApis = {
	overview: fetchNavigatorTicketingOverview,
	volumeTrends: fetchNavigatorTicketingVolumeTrends,
	teamWorkload: fetchNavigatorTicketingTeamWorkload,
	rca: fetchNavigatorTicketingRca,
	lifecycle: fetchNavigatorTicketingLifecycle,
	categories: fetchNavigatorTicketingCategories,
	submittersLocations: fetchNavigatorTicketingSubmittersLocations,
};

export const navigatorTicketingAnalyticsMocks = {
	filters: TICKETING_FILTER_OPTIONS_MOCK,
	overview: TICKETING_OVERVIEW_MOCK,
	volumeTrends: TICKETING_VOLUME_TRENDS_MOCK,
	teamWorkload: TICKETING_TEAM_WORKLOAD_MOCK,
	rca: TICKETING_RCA_MOCK,
	categories: TICKETING_CATEGORIES_MOCK,
	submittersLocations: TICKETING_SUBMITTERS_LOCATIONS_MOCK,
};

